import { formatDateDDMMYYYY, formatDateDDMonYYYY } from '@/utils/textFormatting';
import { getResultTextColor, getResultLabel, shouldShowResult } from '@/utils/checkResultColors';
import { useState, useEffect, useCallback, useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { 
  FileCheck, Clock, CheckCircle, XCircle, User, Building2, 
  ClipboardList, Eye, Play, AlertTriangle, Briefcase, GraduationCap, Users, MapPin,
  Gavel, Shield, CreditCard, Database, TestTube, UserCheck, Send, School, BarChart3, Workflow,
  RefreshCw, Filter, Search, LogOut, PlusCircle, Timer, Calendar, CalendarClock,
  Phone, Mail, FileText, MoreVertical, RotateCcw, ArrowRight, TrendingUp, ExternalLink, X, Download, Truck, CheckSquare, Upload, PlayCircle, Camera, FileEdit, Copy
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import * as XLSX from 'xlsx';
import Sidebar from '@/components/Sidebar';
import NotificationCenter from '@/components/shared/NotificationCenter';
import EmploymentVerificationForm from '@/components/verification/EmploymentVerificationForm';
import EducationVerificationForm from '@/components/verification/EducationVerificationForm';
import AddressVerificationForm from '@/components/verification/AddressVerificationForm';
import CourtCriminalVerificationForm from '@/components/verification/CourtCriminalVerificationForm';
import IDVerificationForm from '@/components/verification/IDVerificationForm';
import ReferenceCheckForm from '@/components/verification/ReferenceCheckForm';
import DrugTestForm from '@/components/verification/DrugTestForm';
import BulkVerification from '@/components/verification/BulkVerification';
import BulkVendorAssignment from '@/components/verification/BulkVendorAssignment';
import BulkEducationVerification from '@/components/verification/BulkEducationVerification';
import TeamVendorReports from '@/components/reports/TeamVendorReports';
import OperationsView from '@/components/operations/OperationsView';
import BulkDocumentUpload from '@/components/shared/BulkDocumentUpload';
import InitiateVerificationEmailDialog from '@/components/shared/InitiateVerificationEmailDialog';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator } from '@/components/ui/dropdown-menu';
import VerificationUpdateDialog from '@/components/shared/VerificationUpdateDialog';
import CompaniesTab from '@/components/admin/CompaniesTab';
import InstitutionsTab from '@/components/admin/InstitutionsTab';

const CHECK_TYPES = [
  { value: 'address_check', label: 'Address Verification', icon: MapPin },
  { value: 'employment_check', label: 'Employment Verification', icon: Briefcase },
  { value: 'education_check', label: 'Education Verification', icon: GraduationCap },
  { value: 'id_check', label: 'ID Verification', icon: Shield },
  { value: 'criminal_court_check', label: 'Criminal Check', icon: Gavel },
  { value: 'court_advocate_check', label: 'Court Advocate Check', icon: Gavel },
  { value: 'credit_check', label: 'Credit Check', icon: Database },
  { value: 'personal_reference', label: 'Personal Reference', icon: Users },
  { value: 'professional_reference', label: 'Professional Reference', icon: Users },
  { value: 'drug_test', label: 'Drug Test', icon: TestTube },
];

const MODE_OF_VERIFICATION = [
  { value: 'phone', label: 'Phone Call', icon: Phone },
  { value: 'email', label: 'Email', icon: Mail },
  { value: 'physical', label: 'Physical Visit', icon: MapPin },
  { value: 'database', label: 'Database Check', icon: Database },
  { value: 'document', label: 'Document Verification', icon: FileText },
  { value: 'video_call', label: 'Video Call', icon: Eye },
];

const STATUS_COLORS = {
  pending: 'bg-yellow-100 text-yellow-800',
  approved: 'bg-blue-100 text-blue-800',
  in_progress: 'bg-orange-100 text-orange-800',
  assigned: 'bg-purple-100 text-purple-800',
  agent_assigned: 'bg-purple-100 text-purple-800',
  vendor_assigned: 'bg-indigo-100 text-indigo-800',
  not_initiated: 'bg-orange-100 text-orange-800',
  verified: 'bg-green-100 text-green-800',
  failed: 'bg-red-100 text-red-800',
  discrepant: 'bg-red-100 text-red-800',
  clear: 'bg-green-100 text-green-800',
  returned: 'bg-amber-100 text-amber-800',
};

const VERIFICATION_RESULTS = [
  { value: 'clear', label: 'Clear - No Issues' },
  { value: 'minor_discrepancy', label: 'Minor Discrepancy' },
  { value: 'major_discrepancy', label: 'Major Discrepancy' },
  { value: 'unable_to_verify', label: 'Unable to Verify' },
  { value: 'pending_response', label: 'Pending Response' },
];

const RETURN_REASONS = [
  { value: 'incorrect_assignment', label: 'Incorrect Assignment' },
  { value: 'missing_documents', label: 'Missing Documents' },
  { value: 'insufficient_data', label: 'Insufficient Data' },
  { value: 'out_of_scope', label: 'Out of Scope' },
  { value: 'requires_vendor', label: 'Requires External Vendor' },
  { value: 'duplicate_check', label: 'Duplicate Check' },
  { value: 'other', label: 'Other' },
];

// Default TAT values (will be overridden by API values from Check Types Management)
const DEFAULT_CHECK_TAT = {
  address_check: 5,
  employment_check: 7,
  education_check: 10,
  id_check: 3,
  criminal_court_check: 7,
  criminal_advocate_check: 5,
  court_advocate_check: 5,
  credit_check: 3,
  reference_check: 5,
  personal_reference: 5,
  professional_reference: 5,
  drug_test: 5,
  global_database_check: 3,
};

export default function InhouseVerificationDashboard({ user, onLogout, onSwitchAccount }) {
  const [activeTab, setActiveTab] = useState('dashboard');
  const [loading, setLoading] = useState(true);
  const [checks, setChecks] = useState([]);
  const [clients, setClients] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [insufficiencies, setInsufficiencies] = useState([]);
  const [myAssignment, setMyAssignment] = useState(null);
  const [checkTypeTAT, setCheckTypeTAT] = useState(DEFAULT_CHECK_TAT); // TAT values from Check Types Management
  
  // Pagination state
  const [pageSize, setPageSize] = useState(20);
  const [currentPage, setCurrentPage] = useState(1);
  
  // Drill-down state
  const [drillDownType, setDrillDownType] = useState(null);
  const [showDrillDown, setShowDrillDown] = useState(false);
  
  // Filters
  const [filters, setFilters] = useState({
    clientId: 'all',
    checkType: 'all',
    status: 'all',
    tatStatus: 'all',
    modeOfVerification: 'all',
    educationType: 'all',
    dateFilterType: 'latest',
    assignedFrom: '',
    assignedTo: '',
    search: '',
  });
  
  // Pagination helper
  const getPaginatedChecks = (filteredChecks) => {
    const startIndex = (currentPage - 1) * pageSize;
    const endIndex = startIndex + pageSize;
    return filteredChecks.slice(startIndex, endIndex);
  };
  
  // Dialog states
  const [selectedCheck, setSelectedCheck] = useState(null);
  const [showUpdateStatus, setShowUpdateStatus] = useState(false);
  const [statusUpdate, setStatusUpdate] = useState({
    status: '',
    mode_of_verification: '',
    remarks: '',
    verification_result: ''
  });
  const [showGenerateReport, setShowGenerateReport] = useState(false);
  const [reportData, setReportData] = useState(null);
  const [showReturnDialog, setShowReturnDialog] = useState(false);
  const [returnData, setReturnData] = useState({ reason: '', notes: '' });
  const [showCheckDetails, setShowCheckDetails] = useState(false);
  
  // Verification form states
  const [employmentVerification, setEmploymentVerification] = useState(null);
  const [employeeData, setEmployeeData] = useState(null);
  const [documents, setDocuments] = useState([]);
  const [educationVerification, setEducationVerification] = useState(null);
  const [addressVerification, setAddressVerification] = useState(null);
  const [criminalVerification, setCriminalVerification] = useState(null);
  const [idVerification, setIdVerification] = useState(null);
  const [referenceCheck, setReferenceCheck] = useState(null);
  const [drugTest, setDrugTest] = useState(null);

  // Bulk selection and auto-initiate
  const [selectedChecks, setSelectedChecks] = useState([]);
  
  // Field Verification View (for viewing data submitted by Field Agents)
  const [showFieldVerificationView, setShowFieldVerificationView] = useState(false);
  const [fieldVerificationData, setFieldVerificationData] = useState(null);
  const [fieldVerificationPhotos, setFieldVerificationPhotos] = useState([]);
  
  // Photo Preview Modal
  const [photoPreview, setPhotoPreview] = useState(null);
  const [showPhotoPreview, setShowPhotoPreview] = useState(false);
  
  const [showAutoInitiateDialog, setShowAutoInitiateDialog] = useState(false);
  const [autoInitiateResults, setAutoInitiateResults] = useState(null);
  const [autoInitiateLoading, setAutoInitiateLoading] = useState(false);
  
  // New Enhanced Email Initiation Dialog
  const [showInitiateEmailDialog, setShowInitiateEmailDialog] = useState(false);
  
  // Bulk Document Upload
  const [showBulkUpload, setShowBulkUpload] = useState(false);
  const [showBulkInitiateDialog, setShowBulkInitiateDialog] = useState(false);
  const [bulkInitiateLoading, setBulkInitiateLoading] = useState(false);
  const [bulkInitiateAutoAssign, setBulkInitiateAutoAssign] = useState(true);
  const [bulkInitiateVendorId, setBulkInitiateVendorId] = useState('');
  const [bulkInitiateSpecialInstructions, setBulkInitiateSpecialInstructions] = useState('');

  // Bulk Reminder state
  const [bulkReminderLoading, setBulkReminderLoading] = useState(false);
  const [showBulkReminderResult, setShowBulkReminderResult] = useState(null);

  // Raise Insufficiency state
  const [showRaiseInsuff, setShowRaiseInsuff] = useState(false);
  const [insuffData, setInsuffData] = useState({ reason: '', details: '' });

  // Transfer to Vendor Team
  const [showTransferDialog, setShowTransferDialog] = useState(false);
  const [transferAgentId, setTransferAgentId] = useState('');
  const [transferReason, setTransferReason] = useState('');
  const [transferLoading, setTransferLoading] = useState(false);
  const [vendorTeamAgents, setVendorTeamAgents] = useState([]);

  // Extra Cost Request
  const [showExtraCostDialog, setShowExtraCostDialog] = useState(false);
  const [extraCostCheck, setExtraCostCheck] = useState(null);
  const [extraCostAmount, setExtraCostAmount] = useState('');
  const [extraCostRemarks, setExtraCostRemarks] = useState('');
  const [extraCostLoading, setExtraCostLoading] = useState(false);

  // Document preview state
  const [checkDocuments, setCheckDocuments] = useState([]);
  const [previewDoc, setPreviewDoc] = useState(null);
  const [loadingDocs, setLoadingDocs] = useState(false);
  const [checkDetailData, setCheckDetailData] = useState(null);
  const [selectedSubcheck, setSelectedSubcheck] = useState(null);

  const menuItems = [
    { id: 'dashboard', label: 'Dashboard', icon: BarChart3, color: 'blue', bgColor: 'bg-blue-50', textColor: 'text-blue-700' },
    { id: 'my-checks', label: 'My Assigned Checks', icon: FileCheck, color: 'purple', bgColor: 'bg-purple-50', textColor: 'text-purple-700' },
    { id: 'all-checks', label: 'All Checks', icon: Eye, color: 'emerald', bgColor: 'bg-emerald-50', textColor: 'text-emerald-700' },
    { id: 'completed', label: 'Completed', icon: CheckCircle, color: 'green', bgColor: 'bg-green-50', textColor: 'text-green-700' },
    { id: 'client-tat', label: 'Client TAT Summary', icon: Clock, color: 'amber', bgColor: 'bg-amber-50', textColor: 'text-amber-700' },
    { id: 'companies', label: 'Company Database', icon: Building2, color: 'indigo', bgColor: 'bg-indigo-50', textColor: 'text-indigo-700' },
    { id: 'institutions', label: 'Universities', icon: GraduationCap, color: 'teal', bgColor: 'bg-teal-50', textColor: 'text-teal-700' },
    { id: 'reports', label: 'Reports', icon: BarChart3, color: 'orange', bgColor: 'bg-orange-50', textColor: 'text-orange-700' },
  ];

  // Fetch check types with TAT from Operations Manager settings
  const fetchCheckTypeTAT = useCallback(async () => {
    try {
      const res = await axios.get('/check-types');
      const tatMap = {};
      (res.data || []).forEach(ct => {
        if (ct.id && ct.default_tat) {
          tatMap[ct.id] = ct.default_tat;
        }
      });
      // Merge with defaults (API values take precedence)
      setCheckTypeTAT(prev => ({ ...prev, ...tatMap }));
    } catch (error) {
      console.log('Using default TAT values');
    }
  }, []);

  useEffect(() => {
    fetchData();
    fetchVendors();
    fetchClients();
    fetchInsufficiencies();
    fetchCheckTypeTAT();
    // Fetch vendor team agents for transfer dialog
    const fetchVendorTeamAgents = async () => {
      try {
        const res = await axios.get('/agent/vendor-team-agents');
        setVendorTeamAgents(res.data || []);
      } catch (err) { console.error('Failed to fetch vendor team agents:', err); }
    };
    fetchVendorTeamAgents();
  }, []);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      // Fetch all checks without filtering params - filtering is done client-side
      const [checksRes, assignmentRes] = await Promise.all([
        axios.get('/team/checks'),
        axios.get('/agent-assignments/me').catch(() => ({ data: null }))
      ]);
      
      let allChecks = checksRes.data || [];
      
      // Enrich with TAT calculation using values from Check Types Management
      allChecks = allChecks.map(check => {
        const checkType = getCheckTypeString(check.check_type);
        // Priority: check.tat_days (from assignment) > checkTypeTAT (from Ops Manager settings) > default
        const clientTat = check.tat_days || checkTypeTAT[checkType] || DEFAULT_CHECK_TAT[checkType] || 7;
        const agentTat = Math.max(clientTat - 2, 1); // Agent TAT = Client TAT - 2 days

        let daysElapsed = 0;
        let isWithinTat = true;
        const isPaused = !!check.tat_paused_at && !check.completed_at;

        if (check.assigned_at) {
          const assignedDate = new Date(check.assigned_at);
          // End cap: completion date, or pause moment if currently paused, else now
          let endDate;
          if (check.completed_at) {
            endDate = new Date(check.completed_at);
          } else if (isPaused) {
            endDate = new Date(check.tat_paused_at);
          } else {
            endDate = new Date();
          }
          const rawDays = (endDate - assignedDate) / (1000 * 60 * 60 * 24);
          // Subtract any previously accumulated pause duration (resumed-then-running cases)
          const pauseDays = Number(check.total_tat_paused_days) || 0;
          daysElapsed = Math.max(0, Math.ceil(rawDays - pauseDays));

          isWithinTat = daysElapsed <= agentTat;
        }

        return { ...check, client_tat: clientTat, agent_tat: agentTat, days_elapsed: daysElapsed, is_within_tat: isWithinTat, is_tat_paused: isPaused };
      });

      setChecks(allChecks);
      setMyAssignment(assignmentRes.data);
    } catch (error) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  }, [checkTypeTAT]);

  const fetchVendors = async () => {
    try {
      const res = await axios.get('/agent/vendors');
      setVendors(res.data || []);
    } catch (error) {}
  };

  const fetchClients = async () => {
    try {
      const res = await axios.get('/clients');
      setClients(res.data || []);
    } catch (error) {}
  };

  const fetchInsufficiencies = async () => {
    try {
      const res = await axios.get('/insufficiencies');
      setInsufficiencies(res.data || []);
    } catch (error) {}
  };

  // Helper functions
  const getCheckTypeString = (checkType) => {
    if (typeof checkType === 'string') return checkType;
    if (checkType && typeof checkType === 'object') return checkType.check_type || 'unknown';
    return 'unknown';
  };

  const getCheckTypeLabel = (checkType) => {
    const typeStr = getCheckTypeString(checkType);
    const found = CHECK_TYPES.find(ct => ct.value === typeStr);
    if (found) return found.label;
    // Capitalize first letter of each word
    return typeStr.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
  };

  const getCheckTypeIcon = (checkType) => {
    const typeStr = getCheckTypeString(checkType);
    const found = CHECK_TYPES.find(ct => ct.value === typeStr);
    return found ? found.icon : FileText;
  };

  const getModeIcon = (mode) => {
    const found = MODE_OF_VERIFICATION.find(m => m.value === mode);
    return found ? found.icon : FileText;
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: '2-digit' });
  };

  // Helper to get check type string for comparison
  const getCheckTypeForFilter = (checkType) => {
    if (typeof checkType === 'string') return checkType;
    if (checkType && typeof checkType === 'object') return checkType.check_type || checkType.id || '';
    return '';
  };

  // Computed data - Client-side filtering for all filters
  const getFilteredChecks = (checksToFilter) => {
    return checksToFilter.filter(check => {
      // Client filter
      if (filters.clientId && filters.clientId !== 'all') {
        if (check.client_id !== filters.clientId) return false;
      }
      // Check type filter
      if (filters.checkType && filters.checkType !== 'all') {
        const checkTypeStr = getCheckTypeForFilter(check.check_type);
        if (checkTypeStr !== filters.checkType) return false;
      }
      // Status filter
      if (filters.status && filters.status !== 'all') {
        const verificationProgress = check.verification_progress || check.status || '';
        const checkStatus = check.status || '';
        if (filters.status === 'not_initiated' && verificationProgress && verificationProgress !== 'not_initiated' && verificationProgress !== 'pending') return false;
        if (filters.status === 'in_progress' && verificationProgress !== 'in_progress' && verificationProgress !== 'initiated') return false;
        if (filters.status === 'completed' && !['completed', 'verified', 'clear', 'report_approved', 'qc_approved', 'field_verification_completed'].includes(verificationProgress) && !['completed', 'verified', 'clear', 'report_approved', 'qc_approved', 'field_verification_completed'].includes(checkStatus) && !check.is_completed) return false;
        if (filters.status === 'insufficiency' && !check.has_insufficiency && verificationProgress !== 'insufficiency') return false;
      }
      // TAT filter
      if (filters.tatStatus && filters.tatStatus !== 'all') {
        if (filters.tatStatus === 'within_tat' && !check.is_within_tat) return false;
        if (filters.tatStatus === 'over_tat' && check.is_within_tat) return false;
      }
      // Mode of verification filter
      if (filters.modeOfVerification && filters.modeOfVerification !== 'all') {
        if (check.mode_of_verification !== filters.modeOfVerification) return false;
      }
      // Education Type filter
      if (filters.educationType && filters.educationType !== 'all') {
        const checkType = getCheckTypeForFilter(check.check_type);
        if (checkType !== 'education_check') return false;
        const eduType = (check.subcheck_type || check.subcheck_data?.education_type || check.subcheck_data?.type || '').toLowerCase();
        const filterVal = filters.educationType.toLowerCase();
        if (!eduType.includes(filterVal)) return false;
      }
      // Search filter
      if (filters.search) {
        const searchLower = filters.search.toLowerCase();
        const eduType = check.subcheck_type || check.subcheck_data?.education_type || '';
        if (!check.employee_name?.toLowerCase().includes(searchLower) &&
            !check.case_id?.toLowerCase().includes(searchLower) &&
            !check.client_name?.toLowerCase().includes(searchLower) &&
            !check.check_id?.toLowerCase().includes(searchLower) &&
            !check.employee_email?.toLowerCase().includes(searchLower) &&
            !eduType.toLowerCase().includes(searchLower)) return false;
      }
      return true;
    });
  };

  // For vendor_management users, show all checks assigned to them or their team
  // For other users, show only checks directly assigned to them
  const isVendorManagement = user?.role === 'vendor_management' || user?.team_type === 'vendor_management' || user?.team_type === 'vendor';
  
  const getMyChecks = () => {
    if (isVendorManagement) {
      // Agent Vendor Team sees all checks that are:
      // 1. Assigned directly to them (current OR original agent ownership
      //    before vendor overwrite — agent_assigned_to is preserved)
      // 2. Assigned to their vendor/team (assigned_vendor matches their vendor_id)
      // 3. Have assignment_type = 'vendor' and are in their team's check types
      return checks.filter(c => 
        c.assigned_to === user.id || 
        c.agent_assigned_to === user.id ||
        c.assigned_vendor?.vendor_id === user.vendor_id ||
        c.vendor_team_id === user.team_id ||
        (c.assignment_type === 'vendor' && ['address_check', 'criminal_check', 'id_verification', 'court_record_check'].includes(c.check_type))
      );
    }
    // Match both current and original agent ownership (agent_assigned_to is
    // preserved when a vendor later overwrites assigned_to).
    return checks.filter(c => c.assigned_to === user.id || c.agent_assigned_to === user.id);
  };
  
  const myChecks = getFilteredChecks(getMyChecks())
    .sort((a, b) => {
      const dateA = new Date(a.assigned_at || a.created_at || 0);
      const dateB = new Date(b.assigned_at || b.created_at || 0);
      return dateB - dateA; // Most recent first
    });
  const allMyChecks = getMyChecks();
  
  // Get unique parent checks for stats (count main checks, not expanded subchecks)
  const uniqueParentChecks = useMemo(() => {
    const parentMap = new Map();
    allMyChecks.forEach(c => {
      const parentId = c.parent_check_id || c.check_id;
      if (!parentMap.has(parentId)) {
        const siblingSubchecks = allMyChecks.filter(sc => (sc.parent_check_id || sc.check_id) === parentId);
        const COMPLETED_STATUSES = ['verified', 'completed', 'clear', 'report_approved', 'qc_approved', 'field_verification_completed'];
        const allCompleted = siblingSubchecks.every(sc => COMPLETED_STATUSES.includes(sc.status) || COMPLETED_STATUSES.includes(sc.verification_progress) || sc.verification_progress === 'completed');
        const anyInsufficiency = siblingSubchecks.some(sc => sc.has_insufficiency || sc.verification_progress === 'insufficiency');
        
        parentMap.set(parentId, {
          ...c,
          parent_check_id: parentId,
          is_completed: allCompleted,
          has_insufficiency: anyInsufficiency,
          subcheck_count: siblingSubchecks.length
        });
      }
    });
    return Array.from(parentMap.values());
  }, [allMyChecks]);
  
  // Stats based on unique parent checks
  const notInitiatedChecks = uniqueParentChecks.filter(c => !c.verification_progress || c.verification_progress === 'not_initiated');
  const inProgressChecks = uniqueParentChecks.filter(c => c.verification_progress === 'in_progress' || c.verification_progress === 'initiated');

  // -----------------------------------------------------------------------
  // Export In-Progress checks (Agent Internal Team) — Jul 19, 2026 feature
  // Produces a 2-sheet workbook:
  //   • Education  → check_type === 'education_check'
  //   • Employment → check_type in ('employment_check', 'reference_check')
  // Only the logged-in agent's assigned In-Progress subchecks are included.
  // -----------------------------------------------------------------------
  const [exportInProgressLoading, setExportInProgressLoading] = useState(false);

  const _fmtDate = (v) => {
    if (!v) return '';
    try {
      const d = new Date(v);
      if (isNaN(d.getTime())) return '';
      const dd = String(d.getDate()).padStart(2, '0');
      const mm = String(d.getMonth() + 1).padStart(2, '0');
      return `${dd}-${mm}-${d.getFullYear()}`;
    } catch {
      return '';
    }
  };

  // Human-readable category label (Education)
  const _eduCategoryLabel = (raw) => {
    const s = (raw || '').toString().toLowerCase().replace(/[_\s-]+/g, '_');
    if (!s) return '';
    if (s.includes('highest')) return 'Highest Education';
    if (s === 'iti' || s.includes('iti')) return 'ITI';
    if (s.includes('prior') || s.includes('before') || s === 'previous_education') return 'Prior to Highest';
    if (s === 'ssc' || s.includes('10th') || s.includes('secondary')) return 'SSC';
    if (s.includes('hsc') || s.includes('12th') || s.includes('senior')) return 'HSC';
    if (s.includes('graduation') || s === 'ug' || s === 'bachelor') return 'Graduation';
    if (s.includes('post') || s === 'pg' || s === 'master') return 'Post Graduation';
    return raw;
  };

  // Human-readable category label (Employment + Reference)
  const _empCategoryLabel = (raw, checkType) => {
    if (checkType === 'reference_check') {
      const s = (raw || '').toString().toLowerCase();
      if (s.includes('professional') || s.includes('prof')) return 'Professional Reference';
      if (s.includes('personal') || s.includes('peer')) return 'Personal Reference';
      return raw ? String(raw) : 'Reference';
    }
    const s = (raw || '').toString().toLowerCase().replace(/[_\s-]+/g, '_');
    if (!s) return '';
    if (s.includes('current')) return 'Current Employment';
    if (s.includes('previous_1') || s === 'previous_employment_1' || s === 'prev_1' || s === 'previous1') return 'Previous Employment 1';
    if (s.includes('previous_2') || s === 'previous_employment_2' || s === 'prev_2' || s === 'previous2') return 'Previous Employment 2';
    if (s.includes('previous')) return 'Previous Employment';
    return raw;
  };

  const _reminderCount = (check) => {
    const arr = check.reminders_sent || check.reminder_log || check.reminders || [];
    if (Array.isArray(arr)) return arr.length;
    if (typeof arr === 'number') return arr;
    return check.reminder_count ?? 0;
  };

  const handleExportInProgress = () => {
    try {
      setExportInProgressLoading(true);
      // Include both In-Progress AND Not-Initiated checks assigned to
      // this agent (Jul 19, 2026 update per user request — agents also
      // want a working list of items still waiting to be kicked off).
      const rows = [...(notInitiatedChecks || []), ...(inProgressChecks || [])].filter(c => c);
      if (rows.length === 0) {
        toast.info('No In-Progress or Not-Initiated cases to export');
        return;
      }

      // Split by check_type. Employment sheet also holds reference_check.
      const eduRows = rows.filter(c => c.check_type === 'education_check');
      const empRows = rows.filter(
        c => c.check_type === 'employment_check' || c.check_type === 'reference_check'
      );

      const buildEducationRow = (c, idx) => {
        const sd = c.subcheck_data || {};
        const clientEntity = c.entity_name
          ? `${c.client_name || ''}${c.client_name ? ' / ' : ''}${c.entity_name}`
          : (c.client_name || '');
        return {
          'Sl no': idx + 1,
          'Sub Check ID': c.check_id || c.id || '',
          'Assigned Date': _fmtDate(c.assigned_at || c.vendor_assigned_at || c.agent_assigned_at || c.created_at),
          'Case ID': c.case_id || '',
          'Subcheck type': c.check_type || '',
          'Client / Entity': clientEntity,
          'Candidate Name': c.employee_name || c.candidate_name || '',
          'Contact no': sd.candidate_phone || c.candidate_phone || c.employee_phone || '',
          'Institution Name': sd.institution_name || sd.college || sd.college_name || '',
          'Degree / Course': sd.course || sd.degree || '',
          'Year of Passing': sd.year_of_passing || sd.passing_year || '',
          'Roll Number': sd.roll_number || sd.registration_number || sd.reg_no || '',
          'Status': c.verification_progress || c.status || '',
          'Initiated date': _fmtDate(c.initiated_at || c.mail_initiated_at || c.assigned_at),
          'University / College Email (if Initiated by Mail)': sd.verification_email || sd.university_email || sd.college_email || sd.institute_email || '',
          'TAT (Days)': c.days_elapsed ?? c.tat_days ?? '',
          'Number of Reminders sent': _reminderCount(c),
          'Category': _eduCategoryLabel(c.sub_option || sd.education_level || sd.level || c.subcheck_type),
        };
      };

      const buildEmploymentRow = (c, idx) => {
        const sd = c.subcheck_data || {};
        const isReference = c.check_type === 'reference_check';
        const clientEntity = c.entity_name
          ? `${c.client_name || ''}${c.client_name ? ' / ' : ''}${c.entity_name}`
          : (c.client_name || '');
        return {
          'Sl no': idx + 1,
          'Sub Check ID': c.check_id || c.id || '',
          'Assigned Date': _fmtDate(c.assigned_at || c.vendor_assigned_at || c.agent_assigned_at || c.created_at),
          'Case ID': c.case_id || '',
          'Subcheck type': c.check_type || '',
          'Client / Entity': clientEntity,
          'Candidate Name': c.employee_name || c.candidate_name || '',
          'Contact No.': sd.candidate_phone || c.candidate_phone || c.employee_phone || '',
          'Employer Name': isReference ? '' : (sd.employer_name || sd.company_name || ''),
          'Designation': isReference ? '' : (sd.designation || sd.position || ''),
          'Employee ID': isReference ? '' : (sd.employee_id || sd.emp_id || ''),
          'From Date': isReference ? '' : _fmtDate(sd.from_date || sd.start_date),
          'To Date': isReference ? '' : _fmtDate(sd.to_date || sd.end_date),
          'Status': c.verification_progress || c.status || '',
          'HR Name': isReference ? '' : (sd.hr_name || sd.hr_contact_name || ''),
          'HR Email (Mail Initiated)': isReference ? '' : (sd.hr_email || sd.verification_email || ''),
          'HR Contact': isReference ? '' : (sd.hr_phone || sd.hr_contact || ''),
          'Reporting Manager': isReference ? '' : (sd.reporting_manager || sd.rm_name || ''),
          'RM Contact number': isReference ? '' : (sd.rm_contact || sd.rm_phone || ''),
          'Referee Name': isReference ? (sd.referee_name || sd.reference_name || sd.name || '') : '',
          'Referee Email': isReference ? (sd.referee_email || sd.reference_email || sd.email || '') : '',
          'Referee Phone': isReference ? (sd.referee_phone || sd.reference_phone || sd.phone || '') : '',
          'TAT (Days)': c.days_elapsed ?? c.tat_days ?? '',
          'Number of Reminders sent': _reminderCount(c),
          'Category': _empCategoryLabel(c.sub_option || sd.employment_type || sd.reference_type || c.subcheck_type, c.check_type),
        };
      };

      const wb = XLSX.utils.book_new();
      const eduSheet = XLSX.utils.json_to_sheet(eduRows.map(buildEducationRow));
      const empSheet = XLSX.utils.json_to_sheet(empRows.map(buildEmploymentRow));
      XLSX.utils.book_append_sheet(wb, eduSheet, 'Education');
      XLSX.utils.book_append_sheet(wb, empSheet, 'Employment & Reference');

      const today = new Date();
      const stamp = `${today.getFullYear()}-${String(today.getMonth()+1).padStart(2,'0')}-${String(today.getDate()).padStart(2,'0')}`;
      const agentSlug = (user?.name || 'agent').toString().replace(/\s+/g, '_');
      XLSX.writeFile(wb, `Open_Cases_${agentSlug}_${stamp}.xlsx`);
      toast.success(`Exported ${eduRows.length} Education + ${empRows.length} Employment/Reference rows (Not-Initiated + In-Progress)`);
    } catch (err) {
      console.error('Export In-Progress error', err);
      toast.error('Failed to export In-Progress cases');
    } finally {
      setExportInProgressLoading(false);
    }
  };

  const newlyAddedChecks = uniqueParentChecks.filter(c => {
    if (!c.assigned_at && !c.created_at) return false;
    const addedDate = new Date(c.assigned_at || c.created_at);
    const hoursDiff = (new Date() - addedDate) / (1000 * 60 * 60);
    return hoursDiff <= 24;
  });
  // Not Sent - checks not yet initiated to HR/University/Reference (check types: employment, education, reference)
  const notSentChecks = uniqueParentChecks.filter(c => {
    const checkType = getCheckTypeString(c.check_type);
    const isRelevantType = ['employment', 'education', 'reference'].some(t => checkType.toLowerCase().includes(t));
    return isRelevantType && (!c.verification_progress || c.verification_progress === 'not_initiated');
  });
  const withinTatChecks = uniqueParentChecks.filter(c => c.is_within_tat && !c.is_completed);
  const overTatChecks = uniqueParentChecks.filter(c => !c.is_within_tat && !c.is_completed);
  const completedChecks = getFilteredChecks(uniqueParentChecks.filter(c => c.is_completed));
  const insufficiencyChecks = uniqueParentChecks.filter(c => c.has_insufficiency);
  
  // Insufficiency count - only count from agent's own checks
  const insuffCount = useMemo(() => {
    return insufficiencyChecks.length;
  }, [insufficiencyChecks]);

  // Client-wise TAT summary
  const clientTatSummary = {};
  myChecks.forEach(check => {
    const clientName = check.client_name || 'Unknown';
    if (!clientTatSummary[clientName]) {
      clientTatSummary[clientName] = { total: 0, within_tat: 0, over_tat: 0, check_types: {} };
    }
    clientTatSummary[clientName].total++;
    if (check.is_within_tat) clientTatSummary[clientName].within_tat++;
    else clientTatSummary[clientName].over_tat++;
    
    const checkType = getCheckTypeString(check.check_type);
    if (!clientTatSummary[clientName].check_types[checkType]) {
      clientTatSummary[clientName].check_types[checkType] = { total: 0, within_tat: 0, over_tat: 0 };
    }
    clientTatSummary[clientName].check_types[checkType].total++;
    if (check.is_within_tat) clientTatSummary[clientName].check_types[checkType].within_tat++;
    else clientTatSummary[clientName].check_types[checkType].over_tat++;
  });

  // Drill-down
  const getDrillDownData = () => {
    switch (drillDownType) {
      case 'total': return { title: 'All Assigned Checks', data: allMyChecks };
      case 'newly_added': return { title: 'Newly Added (24h)', data: newlyAddedChecks };
      case 'not_initiated': return { title: 'Not Initiated Checks', data: notInitiatedChecks };
      case 'not_sent': return { title: 'Not Sent to HR/University/Reference', data: notSentChecks };
      case 'in_progress': return { title: 'In Progress Checks', data: inProgressChecks };
      case 'completed': return { title: 'Completed Checks', data: completedChecks };
      case 'within_tat': return { title: 'Within TAT', data: withinTatChecks };
      case 'over_tat': return { title: 'Over TAT', data: overTatChecks };
      case 'insuff': return { title: 'Insufficiency Checks', data: insufficiencyChecks };
      case 'insuff_raised': return { title: 'Insufficiencies Raised', data: insuffRaised };
      case 'mirrored': return { title: 'Present & Permanent Address Same (Mirrored)', data: [] };
      default: return { title: '', data: [] };
    }
  };

  const handleStatCardClick = (type) => {
    setDrillDownType(type);
    setShowDrillDown(true);
  };

  // Quick date filters
  const applyDateFilter = (filterType) => {
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    let newFilters = { ...filters, dateFilterType: filterType };
    
    switch (filterType) {
      case 'today':
        newFilters.assignedFrom = today.toISOString().split('T')[0];
        newFilters.assignedTo = today.toISOString().split('T')[0];
        break;
      case 'this_week':
        const weekStart = new Date(today);
        weekStart.setDate(today.getDate() - today.getDay());
        newFilters.assignedFrom = weekStart.toISOString().split('T')[0];
        newFilters.assignedTo = today.toISOString().split('T')[0];
        break;
      case 'this_month':
        const monthStart = new Date(today.getFullYear(), today.getMonth(), 1);
        newFilters.assignedFrom = monthStart.toISOString().split('T')[0];
        newFilters.assignedTo = today.toISOString().split('T')[0];
        break;
      default:
        newFilters.assignedFrom = '';
        newFilters.assignedTo = '';
    }
    setFilters(newFilters);
  };

  // Dialog handlers
  const openUpdateStatus = (check) => {
    setSelectedCheck(check);
    setStatusUpdate({
      status: check.status || '',
      mode_of_verification: check.mode_of_verification || '',
      remarks: check.remarks || '',
      verification_result: check.verification_result || ''
    });
    setShowUpdateStatus(true);
  };

  // Auto-Initiate handlers - Now uses enhanced email dialog
  const openAutoInitiate = async (check) => {
    setSelectedCheck(check);
    // Open the new enhanced email initiation dialog
    setShowInitiateEmailDialog(true);
  };

  const handleAutoInitiateEmail = async () => {
    if (!selectedCheck || !autoInitiateResults?.can_auto_initiate) return;
    
    setAutoInitiateLoading(true);
    try {
      const res = await axios.post('/auto-initiate/subcheck', {
        case_id: selectedCheck.case_id,
        check_id: selectedCheck.check_id || selectedCheck.id,
        subcheck_index: selectedCheck.subcheck_index || 0,
        send_email: true
      });
      
      if (res.data.email_sent) {
        toast.success(`Verification email sent to ${res.data.target_email}`);
      } else {
        toast.info(res.data.message || 'Auto-initiate completed');
      }
      
      setShowAutoInitiateDialog(false);
      setSelectedCheck(null);
      fetchData();
    } catch (error) {
      toast.error('Failed to send verification email');
    } finally {
      setAutoInitiateLoading(false);
    }
  };

  const handleUpdateStatus = async () => {
    if (!selectedCheck || !statusUpdate.status) {
      toast.error('Please select a status');
      return;
    }
    try {
      await axios.put(`/team/checks/${selectedCheck.case_id}/${selectedCheck.check_id || selectedCheck.id}/status`, statusUpdate);
      toast.success('Check status updated');
      setShowUpdateStatus(false);
      setSelectedCheck(null);
      fetchData();
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  const openGenerateReport = (check) => {
    setSelectedCheck(check);
    setReportData({
      check_type: getCheckTypeLabel(check.check_type),
      employee_name: check.employee_name,
      status: check.status,
      mode_of_verification: check.mode_of_verification,
      verification_result: check.verification_result,
      remarks: check.remarks,
      verified_at: check.completed_at || check.updated_at
    });
    setShowGenerateReport(true);
  };

  const generateReport = async () => {
    try {
      toast.info('Generating BGV Report...');
      const response = await axios.post(`/cases/${selectedCheck.case_id}/generate-bgv-report`, {
        is_final: false
      }, {
        responseType: 'blob'
      });
      
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      const disposition = response.headers['content-disposition'];
      const match = disposition?.match(/filename="?([^"]+)"?/);
      link.download = match ? match[1] : `BGV_Report_${selectedCheck.case_id}_${new Date().toISOString().slice(0,10)}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      toast.success('Report downloaded successfully');
      setShowGenerateReport(false);
    } catch (error) {
      console.error('Report generation error:', error);
      toast.error(error.response?.data?.detail || 'Failed to generate report');
    }
  };

  const openReturnDialog = (check) => {
    setSelectedCheck(check);
    setReturnData({ reason: '', notes: '' });
    setShowReturnDialog(true);
  };

  const handleReturnCheck = async () => {
    if (!selectedCheck || !returnData.reason) {
      toast.error('Please select a reason');
      return;
    }
    try {
      await axios.post(`/agent/checks/${selectedCheck.case_id}/${selectedCheck.check_id || selectedCheck.id}/return`, returnData);
      toast.success('Check returned to Operations Manager');
      setShowReturnDialog(false);
      fetchData();
    } catch (error) {
      toast.error('Failed to return check');
    }
  };


  const openCheckDetails = async (check) => {
    setSelectedCheck(check);
    setShowCheckDetails(true);
    setLoadingDocs(true);
    setCheckDocuments([]);
    setPreviewDoc(null);
    setCheckDetailData(null);
    setSelectedSubcheck(null);
    
    try {
      const checkId = check.check_id || check.id;
      const res = await axios.get(`/cases/${check.case_id}/checks/${checkId}/details`);
      const docs = res.data.documents || [];
      setCheckDetailData(res.data);
      
      // Also fetch employee documents from documents collection
      const employeeId = res.data?.employee?.id || check.employee_id;
      if (employeeId) {
        try {
          const empDocsRes = await axios.get(`/employees/${employeeId}/documents`);
          const empDocs = empDocsRes.data || [];
          const existingUrls = new Set(docs.map(d => d.file_url || d.url));
          empDocs.forEach(d => {
            if (!existingUrls.has(d.file_url)) {
              docs.push({
                id: d.id,
                document_type: d.document_type,
                original_filename: d.original_filename || d.file_name,
                file_url: d.file_url,
                file_type: d.file_type,
                name: d.original_filename || d.document_type
              });
              existingUrls.add(d.file_url);
            }
          });
        } catch (e) { /* ignore */ }
      }
      
      // Also add declaration_form from employee record if not already present
      const empDecl = res.data?.employee?.declaration_form_url;
      if (empDecl && !docs.some(d => d.document_type === 'declaration_form')) {
        docs.push({
          id: 'declaration_form',
          document_type: 'declaration_form',
          original_filename: 'Candidate Declaration Form',
          file_url: empDecl,
          file_type: 'application/pdf',
          name: 'Declaration Form'
        });
      }
      
      setCheckDocuments(docs);
      if (docs.length > 0) {
        setPreviewDoc(docs[0]);
      }
    } catch (error) {
      console.error('Failed to load check documents:', error);
    } finally {
      setLoadingDocs(false);
    }
  };

  // Open Field Verification View (for viewing data submitted by Field Agents)
  const openFieldVerificationView = async (check) => {
    setSelectedCheck(check);
    setFieldVerificationPhotos([]);
    
    try {
      const checkId = check.check_id || check.id;
      // Get check details with field verification data
      const detailsRes = await axios.get(`/cases/${check.case_id}/checks/${checkId}/details`);
      
      setFieldVerificationData({
        ...check,
        ...detailsRes.data,
        field_verification: check.field_verification || detailsRes.data?.field_verification || detailsRes.data?.address_verification || {}
      });
      
      // Also try to get field verification photos from annexures
      try {
        const photosRes = await axios.get(`/cases/${check.case_id}/checks/${checkId}/annexures`);
        const fieldPhotos = (photosRes.data || []).filter(p => 
          p.annexure_type?.includes('house_photo') || 
          p.annexure_type?.includes('landmark') || 
          p.annexure_type?.includes('field_') ||
          p.source === 'field_agent'
        );
        setFieldVerificationPhotos(fieldPhotos);
      } catch (e) {
        console.error('Error fetching field photos:', e);
      }
      
      setShowFieldVerificationView(true);
    } catch (error) {
      console.error('Error loading field verification data:', error);
      // Still show dialog with available data
      setFieldVerificationData({
        ...check,
        field_verification: check.field_verification || check.address_verification || {}
      });
      setShowFieldVerificationView(true);
    }
  };

  // Helper function to get subchecks based on check type
  const getSubchecks = (checkType, employee) => {
    if (!employee) return [];
    
    switch (checkType) {
      case 'address_check':
        const addresses = [];
        if (employee.present_address) addresses.push({ type: 'Present Address', ...employee.present_address });
        if (employee.permanent_address) addresses.push({ type: 'Permanent Address', ...employee.permanent_address });
        if (employee.additional_addresses) addresses.push(...employee.additional_addresses.map((a, i) => ({ type: `Additional ${i + 1}`, ...a })));
        return addresses;
      case 'education_check':
        return (employee.education_entries || []).map((e, i) => ({ type: e.education_type === 'highest' ? 'Highest' : `Edu ${i + 1}`, ...e }));
      case 'employment_check':
        return (employee.employment_entries || []).map((e, i) => ({ type: e.employment_type === 'current' ? 'Current' : `Previous ${i}`, ...e }));
      case 'reference_check':
      case 'personal_reference':
        return (employee.personal_references || []).map((r, i) => ({ type: `Ref ${i + 1}`, ...r }));
      case 'professional_reference':
        return (employee.professional_references || []).map((r, i) => ({ type: `Prof Ref ${i + 1}`, ...r }));
      default:
        return [];
    }
  };

  // Helper function to render subcheck details
  const renderSubcheckDetails = (subcheck, checkType) => {
    if (!subcheck) return null;
    
    if (checkType === 'address_check') {
      return (
        <div className="grid grid-cols-2 gap-2 text-xs">
          <div><span className="text-gray-500">Address:</span> {subcheck.complete_address || '-'}</div>
          <div><span className="text-gray-500">City:</span> {subcheck.city || '-'}</div>
          <div><span className="text-gray-500">State:</span> {subcheck.state || '-'}</div>
          <div><span className="text-gray-500">Pincode:</span> {subcheck.pincode || '-'}</div>
          {subcheck.address_proof_url && (
            <Button variant="outline" size="sm" className="col-span-2 text-xs" onClick={() => window.open(subcheck.address_proof_url, '_blank')}>
              <FileText className="w-3 h-3 mr-1" />View Proof
            </Button>
          )}
        </div>
      );
    }
    
    if (checkType === 'education_check') {
      return (
        <div className="grid grid-cols-2 gap-2 text-xs">
          <div><span className="text-gray-500">University:</span> {subcheck.university_name || '-'}</div>
          <div><span className="text-gray-500">College:</span> {subcheck.college || '-'}</div>
          <div><span className="text-gray-500">Course:</span> {subcheck.course || '-'}</div>
          <div><span className="text-gray-500">Year:</span> {subcheck.year_of_passing || '-'}</div>
          <div className="col-span-2 flex gap-1 flex-wrap">
            {subcheck.degree_certificate_url && <Button variant="outline" size="sm" className="text-xs" onClick={() => window.open(subcheck.degree_certificate_url, '_blank')}><FileText className="w-3 h-3 mr-1" />Degree</Button>}
            {subcheck.marks_card_url && <Button variant="outline" size="sm" className="text-xs" onClick={() => window.open(subcheck.marks_card_url, '_blank')}><FileText className="w-3 h-3 mr-1" />Marks</Button>}
          </div>
        </div>
      );
    }
    
    if (checkType === 'employment_check') {
      return (
        <div className="grid grid-cols-2 gap-2 text-xs">
          <div><span className="text-gray-500">Company:</span> {subcheck.company_name || '-'}</div>
          <div><span className="text-gray-500">Designation:</span> {subcheck.designation || '-'}</div>
          <div><span className="text-gray-500">From:</span> {subcheck.employed_from || '-'}</div>
          <div><span className="text-gray-500">To:</span> {subcheck.employed_to || 'Present'}</div>
          <div><span className="text-gray-500">RM:</span> {subcheck.rm_name || '-'}</div>
          {(subcheck.rm_contact_number || subcheck.rm_contact || subcheck.rm_phone) && (
            <div><span className="text-gray-500">RM Contact:</span> {subcheck.rm_contact_number || subcheck.rm_contact || subcheck.rm_phone}</div>
          )}
          <div><span className="text-gray-500">RM Email:</span> {subcheck.rm_email || '-'}</div>
          {subcheck.hr_name && (
            <div><span className="text-gray-500">HR Name:</span> {subcheck.hr_name}</div>
          )}
          {(subcheck.hr_contact_number || subcheck.hr_contact || subcheck.hr_phone) && (
            <div><span className="text-gray-500">HR Contact:</span> {subcheck.hr_contact_number || subcheck.hr_contact || subcheck.hr_phone}</div>
          )}
          <div><span className="text-gray-500">HR Email:</span> {subcheck.hr_email || '-'}</div>
          {(subcheck.salary_per_month || subcheck.ctc || subcheck.salary) && (
            <div><span className="text-gray-500">CTC:</span> {subcheck.salary_per_month || subcheck.ctc || subcheck.salary}</div>
          )}
          <div className="col-span-2 flex gap-1 flex-wrap">
            {subcheck.relieving_letter_url && <Button variant="outline" size="sm" className="text-xs" onClick={() => window.open(subcheck.relieving_letter_url, '_blank')}><FileText className="w-3 h-3 mr-1" />Relieving</Button>}
            {subcheck.latest_payslip_url && <Button variant="outline" size="sm" className="text-xs" onClick={() => window.open(subcheck.latest_payslip_url, '_blank')}><FileText className="w-3 h-3 mr-1" />Payslip</Button>}
          </div>
        </div>
      );
    }
    
    return <p className="text-xs text-gray-500">No details available</p>;
  };

  // Verification form openers
  const openEmploymentVerification = async (check) => {
    try {
      const checkId = check.check_id || check.id;
      if (!check.case_id || !checkId) {
        toast.error('Invalid check data');
        return;
      }
      const res = await axios.get(`/cases/${check.case_id}/checks/${checkId}/verification-details`);
      setEmployeeData(res.data.employee);
      setDocuments(res.data.documents || []);
      setEmploymentVerification({ caseId: check.case_id, checkId, checkData: res.data.check });
      setActiveTab('employment-verification');
    } catch (error) {
      console.error('Failed to load verification details:', error);
      // Use check's subcheck_data as fallback
      if (check.subcheck_data) {
        setEmploymentVerification({ caseId: check.case_id, checkId: check.check_id || check.id, checkData: check });
        setActiveTab('employment-verification');
      } else {
        toast.error('Failed to load verification details');
      }
    }
  };

  const openEducationVerification = async (check) => {
    try {
      const checkId = check.check_id || check.id;
      if (!check.case_id || !checkId) {
        toast.error('Invalid check data');
        return;
      }
      const res = await axios.get(`/cases/${check.case_id}/checks/${checkId}/education-verification-details`);
      setEmployeeData(res.data.employee);
      setDocuments(res.data.documents || []);
      setEducationVerification({ caseId: check.case_id, checkId, checkData: res.data.check });
      setActiveTab('education-verification');
    } catch (error) {
      console.error('Failed to load verification details:', error);
      if (check.subcheck_data) {
        setEducationVerification({ caseId: check.case_id, checkId: check.check_id || check.id, checkData: check });
        setActiveTab('education-verification');
      } else {
        toast.error('Failed to load verification details');
      }
    }
  };

  const openAddressVerification = async (check) => {
    try {
      const checkId = check.check_id || check.id;
      if (!check.case_id || !checkId) {
        toast.error('Invalid check data');
        return;
      }
      const res = await axios.get(`/cases/${check.case_id}/checks/${checkId}/address-verification-details`);
      setEmployeeData(res.data.employee);
      setDocuments(res.data.documents || []);
      setAddressVerification({ caseId: check.case_id, checkId, checkData: res.data.check, clientName: res.data.client?.company_name, entityName: res.data.entity?.name });
      setActiveTab('address-verification');
    } catch (error) {
      console.error('Failed to load verification details:', error);
      if (check.subcheck_data) {
        setAddressVerification({ caseId: check.case_id, checkId: check.check_id || check.id, checkData: check, clientName: check.client_name, entityName: check.entity_name });
        setActiveTab('address-verification');
      } else {
        toast.error('Failed to load verification details');
      }
    }
  };

  const openGenericVerification = async (check, tabName, setStateFn) => {
    try {
      const checkId = check.check_id || check.id;
      if (!check.case_id || !checkId) {
        toast.error('Invalid check data');
        return;
      }
      const res = await axios.get(`/cases/${check.case_id}/checks/${checkId}/address-verification-details`);
      setEmployeeData(res.data.employee);
      setDocuments(res.data.documents || []);
      setStateFn({ caseId: check.case_id, checkId, checkData: res.data.check, checkType: check.check_type });
      setActiveTab(tabName);
    } catch (error) {
      console.error('Failed to load verification details:', error);
      // Fallback to using check data directly
      setStateFn({ caseId: check.case_id, checkId: check.check_id || check.id, checkData: check, checkType: check.check_type });
      setActiveTab(tabName);
    }
  };

  // Render Client TAT Summary as standalone tab
  const renderClientTatSummary = () => (
    <div className="space-y-4">
      <Card className="border-2 border-amber-100">
        <CardHeader className="pb-2 bg-gradient-to-r from-amber-50 to-orange-50">
          <CardTitle className="text-lg flex items-center gap-2 text-amber-800">
            <Clock className="w-5 h-5" />
            Client-wise Check TAT Summary
            <Badge variant="outline" className="ml-2 text-xs">Agent TAT = Client TAT - 2 days</Badge>
          </CardTitle>
        </CardHeader>
        <CardContent>
          {Object.keys(clientTatSummary).length === 0 ? (
            <div className="text-center py-8 text-gray-500">No data available</div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow className="bg-gradient-to-r from-indigo-600 to-blue-600">
                  <TableHead className="text-white font-semibold">Client</TableHead>
                  <TableHead className="text-center text-white font-semibold">Total Checks</TableHead>
                  <TableHead className="text-center text-white font-semibold">Within TAT</TableHead>
                  <TableHead className="text-center text-white font-semibold">Over TAT</TableHead>
                  <TableHead className="text-white font-semibold">Check Type Breakdown</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {Object.entries(clientTatSummary).map(([client, data]) => (
                  <TableRow key={client} className="hover:bg-slate-50">
                    <TableCell className="font-medium">{client}</TableCell>
                    <TableCell className="text-center"><Badge variant="outline">{data.total}</Badge></TableCell>
                    <TableCell className="text-center"><Badge className="bg-green-100 text-green-800">{data.within_tat}</Badge></TableCell>
                    <TableCell className="text-center"><Badge className="bg-red-100 text-red-800">{data.over_tat}</Badge></TableCell>
                    <TableCell>
                      <div className="flex flex-wrap gap-1">
                        {Object.entries(data.check_types).map(([type, counts]) => (
                          <Badge key={type} variant="outline" className="text-xs">{getCheckTypeLabel(type)}: {counts.within_tat}/{counts.total}</Badge>
                        ))}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );

  // Render Dashboard
  const renderDashboard = () => (
    <div className="space-y-6">
      {/* Stats Cards - Order: Total Assigned, Newly Added, Not Initiated, Not Sent, In Progress, Completed, Within TAT, Over TAT, Insuff */}
      {/* Stats count PARENT checks (unique), not expanded subchecks */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-4">
        <Card className="border-blue-200 bg-gradient-to-br from-blue-50 to-blue-100 cursor-pointer hover:shadow-lg transition-all border-l-4 border-l-blue-500" onClick={() => handleStatCardClick('total')}>
          <CardContent className="pt-4">
            <div className="flex items-center gap-3">
              <PlusCircle className="w-8 h-8 text-blue-600" />
              <div>
                <p className="text-2xl font-bold text-blue-900">{uniqueParentChecks.length}</p>
                <p className="text-xs text-blue-700 font-medium">Total Assigned</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-purple-200 bg-gradient-to-br from-purple-50 to-purple-100 cursor-pointer hover:shadow-lg transition-all border-l-4 border-l-purple-500" onClick={() => handleStatCardClick('newly_added')}>
          <CardContent className="pt-4">
            <div className="flex items-center gap-3">
              <PlusCircle className="w-8 h-8 text-purple-600" />
              <div>
                <p className="text-2xl font-bold text-purple-900">{newlyAddedChecks.length}</p>
                <p className="text-xs text-purple-700 font-medium">Newly Added</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-orange-200 bg-gradient-to-br from-orange-50 to-orange-100 cursor-pointer hover:shadow-lg transition-all border-l-4 border-l-orange-500" onClick={() => handleStatCardClick('not_initiated')}>
          <CardContent className="pt-4">
            <div className="flex items-center gap-3">
              <ClipboardList className="w-8 h-8 text-orange-600" />
              <div>
                <p className="text-2xl font-bold text-orange-900">{notInitiatedChecks.length}</p>
                <p className="text-xs text-orange-700 font-medium">Not Initiated</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-rose-200 bg-gradient-to-br from-rose-50 to-rose-100 cursor-pointer hover:shadow-lg transition-all border-l-4 border-l-rose-500" onClick={() => handleStatCardClick('not_sent')}>
          <CardContent className="pt-4">
            <div className="flex items-center gap-3">
              <Send className="w-8 h-8 text-rose-600" />
              <div>
                <p className="text-2xl font-bold text-rose-900">{notSentChecks.length}</p>
                <p className="text-xs text-rose-700 font-medium">Not Sent</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-blue-200 bg-gradient-to-br from-blue-50 to-cyan-100 cursor-pointer hover:shadow-lg transition-all border-l-4 border-l-cyan-500" onClick={() => handleStatCardClick('in_progress')}>
          <CardContent className="pt-4">
            <div className="flex items-center gap-3">
              <PlayCircle className="w-8 h-8 text-cyan-600" />
              <div>
                <p className="text-2xl font-bold text-cyan-900">{inProgressChecks.length}</p>
                <p className="text-xs text-cyan-700 font-medium">In Progress</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-emerald-200 bg-gradient-to-br from-emerald-50 to-emerald-100 cursor-pointer hover:shadow-lg transition-all border-l-4 border-l-emerald-500" onClick={() => handleStatCardClick('completed')}>
          <CardContent className="pt-4">
            <div className="flex items-center gap-3">
              <CheckCircle className="w-8 h-8 text-emerald-600" />
              <div>
                <p className="text-2xl font-bold text-emerald-900">{completedChecks.length}</p>
                <p className="text-xs text-emerald-700 font-medium">Completed</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-green-200 bg-gradient-to-br from-green-50 to-green-100 cursor-pointer hover:shadow-lg transition-all border-l-4 border-l-green-500" onClick={() => handleStatCardClick('within_tat')}>
          <CardContent className="pt-4">
            <div className="flex items-center gap-3">
              <Timer className="w-8 h-8 text-green-600" />
              <div>
                <p className="text-2xl font-bold text-green-900">{withinTatChecks.length}</p>
                <p className="text-xs text-green-700 font-medium">Within TAT</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-red-200 bg-gradient-to-br from-red-50 to-red-100 cursor-pointer hover:shadow-lg transition-all border-l-4 border-l-red-500" onClick={() => handleStatCardClick('over_tat')}>
          <CardContent className="pt-4">
            <div className="flex items-center gap-3">
              <XCircle className="w-8 h-8 text-red-600" />
              <div>
                <p className="text-2xl font-bold text-red-900">{overTatChecks.length}</p>
                <p className="text-xs text-red-700 font-medium">Over TAT</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-amber-200 bg-gradient-to-br from-amber-50 to-amber-100 cursor-pointer hover:shadow-lg transition-all border-l-4 border-l-amber-500" onClick={() => handleStatCardClick('insuff')}>
          <CardContent className="pt-4">
            <div className="flex items-center gap-3">
              <AlertTriangle className="w-8 h-8 text-amber-600" />
              <div>
                <p className="text-2xl font-bold text-amber-900">{insuffCount}</p>
                <p className="text-xs text-amber-700 font-medium">Insuff</p>
              </div>
            </div>
          </CardContent>
        </Card>
        {/* Address Same stat card removed - only relevant for vendor team dashboard */}
      </div>

      {/* Quick Filters & Filters */}
      <Card>
        <CardContent className="pt-4">
          <div className="flex flex-wrap gap-2 mb-4">
            <Label className="text-sm font-medium mr-2">Quick Filters:</Label>
            {['latest', 'today', 'this_week', 'this_month'].map(type => (
              <Button key={type} size="sm" variant={filters.dateFilterType === type ? 'default' : 'outline'}
                onClick={() => applyDateFilter(type)} className={filters.dateFilterType === type ? 'bg-indigo-600' : ''}>
                {type === 'latest' && <RefreshCw className="w-3 h-3 mr-1" />}
                {type === 'today' && <Calendar className="w-3 h-3 mr-1" />}
                {type === 'this_week' && <CalendarClock className="w-3 h-3 mr-1" />}
                {type === 'this_month' && <BarChart3 className="w-3 h-3 mr-1" />}
                {type.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())}
              </Button>
            ))}
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-4 lg:grid-cols-7 gap-3">
            <Select value={filters.clientId} onValueChange={(v) => setFilters({...filters, clientId: v})}>
              <SelectTrigger><SelectValue placeholder="Client" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Clients</SelectItem>
                {clients.map(c => (<SelectItem key={c.id} value={c.id}>{c.company_name || c.name}</SelectItem>))}
              </SelectContent>
            </Select>
            <Select value={filters.checkType} onValueChange={(v) => setFilters({...filters, checkType: v})}>
              <SelectTrigger><SelectValue placeholder="Check Type" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Types</SelectItem>
                {CHECK_TYPES.map(ct => (<SelectItem key={ct.value} value={ct.value}>{ct.label}</SelectItem>))}
              </SelectContent>
            </Select>
            <Select value={filters.status} onValueChange={(v) => setFilters({...filters, status: v})}>
              <SelectTrigger><SelectValue placeholder="Status" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="not_initiated">Not Initiated</SelectItem>
                <SelectItem value="in_progress">In Progress</SelectItem>
                <SelectItem value="completed">Completed</SelectItem>
                <SelectItem value="insufficiency">Insufficiency</SelectItem>
              </SelectContent>
            </Select>
            <Select value={filters.tatStatus} onValueChange={(v) => setFilters({...filters, tatStatus: v})}>
              <SelectTrigger><SelectValue placeholder="TAT Status" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All TAT</SelectItem>
                <SelectItem value="within_tat">Within TAT</SelectItem>
                <SelectItem value="over_tat">Over TAT</SelectItem>
              </SelectContent>
            </Select>
            <Select value={filters.educationType} onValueChange={(v) => setFilters({...filters, educationType: v})}>
              <SelectTrigger data-testid="filter-education-type"><SelectValue placeholder="Education Type" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Education Types</SelectItem>
                <SelectItem value="highest">Highest Education</SelectItem>
                <SelectItem value="ssc">SSC (10th)</SelectItem>
                <SelectItem value="hsc">HSC (12th)</SelectItem>
                <SelectItem value="iti">ITI</SelectItem>
                <SelectItem value="diploma">Diploma</SelectItem>
                <SelectItem value="graduation">Graduation</SelectItem>
                <SelectItem value="post graduation">Post Graduation</SelectItem>
              </SelectContent>
            </Select>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
              <Input placeholder="Search Name, ID, SSC, HSC..." className="pl-10" value={filters.search}
                onChange={(e) => setFilters({...filters, search: e.target.value})}
                onKeyDown={(e) => e.key === 'Enter' && fetchData()} data-testid="search-input" />
            </div>
            <div className="flex gap-2">
              <Button onClick={fetchData} className="bg-indigo-600 hover:bg-indigo-700">
                <Filter className="w-4 h-4 mr-2" />Apply
              </Button>
              <Button 
                variant="outline" 
                onClick={() => {
                  setFilters({
                    clientId: 'all',
                    checkType: 'all',
                    status: 'all',
                    tatStatus: 'all',
                    educationType: 'all',
                    search: '',
                    dateFilterType: 'latest',
                    assignedFrom: '',
                    assignedTo: '',
                  });
                  fetchData();
                }}
              >
                <RotateCcw className="w-4 h-4 mr-2" />Clear
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* My Checks Table */}
      {renderChecksTable(myChecks, 'My Assigned Checks', true)}
    </div>
  );

  // Multiselect handlers
  const toggleCheckSelection = (checkId) => {
    setSelectedChecks(prev => 
      prev.includes(checkId) 
        ? prev.filter(id => id !== checkId) 
        : [...prev, checkId]
    );
  };

  const toggleSelectAll = (checksToSelect) => {
    const allIds = checksToSelect.map(c => c.check_id || c.id);
    const allSelected = allIds.every(id => selectedChecks.includes(id));
    if (allSelected) {
      setSelectedChecks(prev => prev.filter(id => !allIds.includes(id)));
    } else {
      setSelectedChecks(prev => [...new Set([...prev, ...allIds])]);
    }
  };

  const handleBulkInitiate = async () => {
    if (selectedChecks.length === 0) {
      toast.error('No checks selected');
      return;
    }
    if (!bulkInitiateAutoAssign && !bulkInitiateVendorId) {
      toast.error('Please select a vendor to assign checks to');
      return;
    }
    setBulkInitiateLoading(true);
    try {
      const results = { success: 0, failed: 0 };
      for (const checkId of selectedChecks) {
        try {
          // Find the check to get case_id
          const check = checks.find(c => (c.check_id || c.id) === checkId);
          if (!check) {
            results.failed++;
            continue;
          }
          
          if (bulkInitiateAutoAssign) {
            // Auto-assign mode - just initiate
            await axios.post(`/checks/${checkId}/initiate`, {
              initiated_by: user?.id,
              initiated_at: new Date().toISOString()
            });
          } else {
            // Manual assign mode - assign to selected vendor with special instructions
            await axios.post(`/cases/${check.case_id}/checks/${checkId}/assign`, {
              vendor_id: bulkInitiateVendorId,
              assigned_by: user?.id,
              assignment_type: 'vendor',
              special_instructions: bulkInitiateSpecialInstructions
            });
          }
          results.success++;
        } catch (error) {
          console.error(`Failed to process check ${checkId}:`, error);
          results.failed++;
        }
      }
      toast.success(`Bulk ${bulkInitiateAutoAssign ? 'initiation' : 'assignment'} complete: ${results.success} succeeded, ${results.failed} failed`);
      setSelectedChecks([]);
      setShowBulkInitiateDialog(false);
      setBulkInitiateVendorId('');
      setBulkInitiateSpecialInstructions('');
      fetchData();
    } catch (error) {
      toast.error('Bulk operation failed');
    } finally {
      setBulkInitiateLoading(false);
    }
  };

  // Transfer selected checks to vendor team agent
  const handleTransferToVendor = async () => {
    if (selectedChecks.length === 0) {
      toast.error('No checks selected');
      return;
    }
    if (!transferAgentId) {
      toast.error('Please select a vendor team agent');
      return;
    }
    setTransferLoading(true);
    try {
      const results = { success: 0, failed: 0 };
      
      for (const checkId of selectedChecks) {
        const check = myChecks.find(c => (c.check_id || c.id) === checkId);
        if (!check) { results.failed++; continue; }
        try {
          await axios.post(`/agent/checks/${check.case_id}/${checkId}/transfer-to-agent`, {
            agent_id: transferAgentId,
            reason: transferReason || 'Transferred to vendor team agent',
          });
          results.success++;
        } catch (err) {
          console.error(`Failed to transfer check ${checkId}:`, err);
          results.failed++;
        }
      }

      toast.success(`Transfer complete: ${results.success} transferred${results.failed > 0 ? `, ${results.failed} failed` : ''}`);
      setSelectedChecks([]);
      setShowTransferDialog(false);
      setTransferAgentId('');
      setTransferReason('');
      fetchData();
    } catch (error) {
      toast.error('Transfer failed');
    } finally {
      setTransferLoading(false);
    }
  };

  // Bulk Reminder handler — STRICTLY honors current selection.
  // Disabled in UI when nothing is selected; this is a defensive guard for
  // any direct invocation. We never silently fall back to "send to all" — the
  // user must explicitly select rows (May 11, 2026 production safeguard
  // request — HRs were getting flooded when agents misread the button).
  const handleBulkReminder = async () => {
    if (!Array.isArray(selectedChecks) || selectedChecks.length === 0) {
      toast.info('Select one or more rows first, then click Send Reminders');
      return;
    }
    setBulkReminderLoading(true);
    try {
      const payload = { check_ids: selectedChecks };
      const res = await axios.post('/verification-email/send-bulk-reminders', payload);
      const data = res.data;
      setShowBulkReminderResult(data);
      if (data.sent > 0) {
        toast.success(`Reminders sent: ${data.sent} emails`);
      } else if (data.skipped_recent > 0) {
        toast.info(`No reminders sent — all ${data.skipped_recent} eligible checks were emailed within the last 2 days`);
      } else {
        toast.info('No eligible checks found for reminders');
      }
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send bulk reminders');
    } finally {
      setBulkReminderLoading(false);
    }
  };

  // Raise Insufficiency handlers
  const openRaiseInsuff = (check) => {
    setSelectedCheck(check);
    setInsuffData({ reason: '', details: '' });
    setShowRaiseInsuff(true);
  };

  const handleRaiseInsuff = async () => {
    if (!selectedCheck || !insuffData.reason) {
      toast.error('Please select a reason');
      return;
    }
    try {
      await axios.post(`/insufficiencies`, {
        case_id: selectedCheck.case_id,
        check_id: selectedCheck.raw_check_id || selectedCheck.parent_check_id || selectedCheck.check_id || selectedCheck.id,
        check_type: selectedCheck.check_type,
        reason: insuffData.reason,
        details: insuffData.details,
        raised_by: user?.id,
        status: 'open'
      });
      toast.success('Insufficiency raised successfully');
      setShowRaiseInsuff(false);
      setSelectedCheck(null);
      fetchData();
    } catch (error) {
      toast.error('Failed to raise insufficiency');
    }
  };

  const handleRaiseExtraCost = async () => {
    if (!extraCostCheck || !extraCostAmount) return;
    setExtraCostLoading(true);
    try {
      await axios.post('/extra-cost/request', {
        case_id: extraCostCheck.case_id,
        check_id: extraCostCheck.check_id || extraCostCheck.id,
        amount: parseFloat(extraCostAmount),
        remarks: extraCostRemarks
      });
      toast.success('Extra cost request submitted to admin');
      setShowExtraCostDialog(false);
      setExtraCostAmount('');
      setExtraCostRemarks('');
      setExtraCostCheck(null);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to submit request');
    } finally {
      setExtraCostLoading(false);
    }
  };

  // Render Checks Table with Multiselect - Each sub-check displayed as a SEPARATE ROW
  const renderChecksTable = (checksToShow, title, showActions = true) => {
    // Use checksToShow directly - each sub-check gets its own row (no grouping)
    const paginatedChecks = getPaginatedChecks(checksToShow);
    const totalPages = Math.ceil(checksToShow.length / pageSize) || 1;
    const allCheckIds = paginatedChecks.map(c => c.check_id || c.id);
    const allSelected = allCheckIds.length > 0 && allCheckIds.every(id => selectedChecks.includes(id));
    const selectedCount = selectedChecks.filter(id => allCheckIds.includes(id)).length;
    
    return (
    <Card>
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center gap-2">
            <FileCheck className="w-5 h-5" />{title}
          </CardTitle>
          <div className="flex items-center gap-4">
            {/* Bulk Action Bar */}
            {selectedCount > 0 && (
              <div className="flex items-center gap-2 bg-blue-100 px-3 py-1.5 rounded-lg">
                <CheckSquare className="w-4 h-4 text-blue-600" />
                <span className="text-sm font-medium text-blue-800">{selectedCount} selected</span>
                <Button size="sm" className="h-7 bg-green-600 hover:bg-green-700 text-white" onClick={() => setShowBulkInitiateDialog(true)} data-testid="bulk-initiate-btn">
                  <Send className="w-3 h-3 mr-1" />Bulk Initiate ({selectedCount})
                </Button>
                <Button size="sm" className="h-7 bg-purple-600 hover:bg-purple-700 text-white" onClick={() => setShowTransferDialog(true)} data-testid="transfer-to-vendor-btn">
                  <Truck className="w-3 h-3 mr-1" />Transfer to Vendor
                </Button>
                <Button size="sm" variant="outline" className="h-7" onClick={() => setSelectedChecks([])}>Clear</Button>
              </div>
            )}
            <div className="flex items-center gap-2">
              <span className="text-sm text-gray-500">Show:</span>
              <Select value={pageSize.toString()} onValueChange={(v) => { setPageSize(Number(v)); setCurrentPage(1); }}>
                <SelectTrigger className="w-20 h-8"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="20">20</SelectItem>
                  <SelectItem value="50">50</SelectItem>
                  <SelectItem value="100">100</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <Badge variant="outline" className="bg-blue-50 text-blue-700">
              {checksToShow.length} records (sub-checks)
            </Badge>
            <Button onClick={() => setShowBulkUpload(true)} variant="outline" size="sm" className="bg-purple-50 text-purple-700 border-purple-200 hover:bg-purple-100">
              <Upload className="w-4 h-4 mr-2" />Bulk Upload
            </Button>
            <Button 
              onClick={handleBulkReminder} 
              disabled={bulkReminderLoading || selectedCount === 0}
              variant="outline" 
              size="sm" 
              className="bg-amber-50 text-amber-700 border-amber-200 hover:bg-amber-100 disabled:opacity-40 disabled:cursor-not-allowed"
              title={selectedCount === 0 ? 'Select one or more rows to send reminders' : `Send reminders to ${selectedCount} selected record(s)`}
              data-testid="bulk-reminder-btn"
            >
              <Mail className="w-4 h-4 mr-2" />
              {bulkReminderLoading
                ? 'Sending...'
                : `Send Reminders${selectedCount > 0 ? ` (${selectedCount})` : ''}`}
            </Button>
            <Button onClick={fetchData} variant="outline" size="sm"><RefreshCw className="w-4 h-4 mr-2" />Refresh</Button>
          </div>
        </div>
      </CardHeader>
      <CardContent className="p-0">
        {/* Pagination - above table */}
        {checksToShow.length > 0 && (
          <div className="flex items-center justify-between px-4 py-2 border-b bg-slate-50" data-testid="pagination-bar">
            <div className="text-sm text-gray-600">
              Showing {Math.min((currentPage - 1) * pageSize + 1, checksToShow.length)} to {Math.min(currentPage * pageSize, checksToShow.length)} of {checksToShow.length} records
            </div>
            <div className="flex items-center gap-2">
              <Button variant="outline" size="sm" onClick={() => setCurrentPage(1)} disabled={currentPage === 1}>First</Button>
              <Button variant="outline" size="sm" onClick={() => setCurrentPage(p => Math.max(1, p - 1))} disabled={currentPage === 1}>Previous</Button>
              <span className="text-sm px-3">Page {currentPage} of {totalPages}</span>
              <Button variant="outline" size="sm" onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))} disabled={currentPage >= totalPages}>Next</Button>
              <Button variant="outline" size="sm" onClick={() => setCurrentPage(totalPages)} disabled={currentPage >= totalPages}>Last</Button>
            </div>
          </div>
        )}
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="bg-gradient-to-r from-indigo-600 to-blue-600">
                <TableHead className="w-[40px] text-white">
                  <Checkbox 
                    checked={allSelected}
                    onCheckedChange={() => toggleSelectAll(paginatedChecks)}
                    className="border-white data-[state=checked]:bg-white data-[state=checked]:text-indigo-600"
                  />
                </TableHead>
                <TableHead className="w-[100px] text-white font-semibold">Case ID</TableHead>
                <TableHead className="w-[80px] text-white font-semibold">Check ID</TableHead>
                <TableHead className="text-white font-semibold">Employee</TableHead>
                <TableHead className="text-white font-semibold">Client</TableHead>
                <TableHead className="text-white font-semibold">Check Type</TableHead>
                <TableHead className="text-white font-semibold">Mode</TableHead>
                <TableHead className="text-white font-semibold">Status</TableHead>
                <TableHead className="text-white font-semibold">Vendor</TableHead>
                <TableHead className="text-white font-semibold"><div className="text-xs">Agent</div><div className="text-[10px] text-white/70">Remarks</div></TableHead>
                <TableHead className="text-center text-white font-semibold"><div className="text-xs">Initiated</div><div className="text-[10px] text-white/70">Email Sent</div></TableHead>
                <TableHead className="text-center text-white font-semibold"><div className="text-xs">Assigned/Sent</div><div className="text-[10px] text-white/70">To Vendor/Mails</div></TableHead>
                <TableHead className="text-center text-white font-semibold"><div className="text-xs">Completed</div><div className="text-[10px] text-white/70">Verified</div></TableHead>
                <TableHead className="text-center text-white font-semibold"><div className="text-xs">TAT</div><div className="text-[10px] text-white/70">Days/Limit</div></TableHead>
                {showActions && <TableHead className="w-[180px] text-white font-semibold">Actions</TableHead>}
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow><TableCell colSpan={15} className="text-center py-8">Loading...</TableCell></TableRow>
              ) : paginatedChecks.length === 0 ? (
                <TableRow><TableCell colSpan={15} className="text-center py-8 text-gray-500">No checks found</TableCell></TableRow>
              ) : paginatedChecks.map((check, idx) => {
                const ModeIcon = getModeIcon(check.mode_of_verification || check.initiated_mode);
                const CheckIcon = getCheckTypeIcon(check.check_type);
                const isOverTat = !check.is_within_tat && check.status !== 'verified' && check.status !== 'clear';
                const checkId = check.check_id || check.id;
                const isSelected = selectedChecks.includes(checkId);
                
                const getAssignmentDateInfo = () => {
                  if (check.assignment_type === 'vendor' || check.status === 'vendor_assigned') {
                    return { date: check.vendor_assigned_at || check.assigned_at, label: 'To Vendor', color: 'text-indigo-600' };
                  } else if (check.mail_sent_at || check.mails_sent_at) {
                    return { date: check.mail_sent_at || check.mails_sent_at, label: 'Mails Sent', color: 'text-blue-600' };
                  } else if (check.assigned_at) {
                    return { date: check.assigned_at, label: 'Assigned', color: 'text-purple-600' };
                  }
                  return { date: null, label: '-', color: 'text-gray-400' };
                };
                const assignmentInfo = getAssignmentDateInfo();
                
                return (
                  <TableRow key={idx} className={`hover:bg-slate-50 ${isOverTat ? 'bg-red-50/30' : ''} ${isSelected ? 'bg-blue-50' : ''}`}>
                    <TableCell>
                      <Checkbox 
                        checked={isSelected}
                        onCheckedChange={() => toggleCheckSelection(checkId)}
                      />
                    </TableCell>
                    <TableCell className="font-mono text-xs">{check.case_id}</TableCell>
                    <TableCell className="font-mono text-xs text-blue-600">{check.check_id || check.id || '-'}</TableCell>
                    <TableCell>
                      <div><p className="font-medium text-sm">{check.employee_name}</p><p className="text-xs text-gray-500">{check.employee_email?.slice(0, 20)}</p></div>
                    </TableCell>
                    <TableCell className="text-sm">{check.client_name || '-'}</TableCell>
                    <TableCell>
                      <div className="flex flex-col gap-0.5">
                        <div className="flex items-center gap-1">
                          <CheckIcon className="w-3 h-3 text-gray-500" />
                          <span className="text-xs font-medium">{getCheckTypeLabel(check.check_type)}</span>
                        </div>
                        {(check.subcheck_type || check.sub_check_type) && (
                          <span className="text-[10px] text-indigo-600 bg-indigo-50 px-1.5 py-0.5 rounded">{check.subcheck_type || check.sub_check_type}</span>
                        )}
                        {check.permanent_same_as_present && check.sub_option === 'permanent' && (
                          <span data-testid={`same-as-present-badge-${check.check_id}`} className="text-[10px] text-blue-600 bg-blue-50 px-1.5 py-0.5 rounded border border-blue-200">
                            Same as Present - Mirrored
                          </span>
                        )}
                        {check.synced_from_present && (
                          <span className="text-[9px] text-blue-400 italic">Auto-synced</span>
                        )}
                        {check.address_updated_after_unlink && (
                          <span data-testid={`address-updated-badge-${check.check_id}`} className="text-[10px] text-amber-700 bg-amber-50 px-1.5 py-0.5 rounded border border-amber-300 font-semibold">
                            Updated address — re-verify
                          </span>
                        )}
                        {check.verification_item && (
                          <span className="text-[10px] text-gray-500 truncate max-w-[150px]">{check.verification_item}</span>
                        )}
                        {(() => {
                          const ct = (check.check_type || '').toLowerCase();
                          const subData = check.subcheck_data || {};
                          let hoverLabel = '';
                          if (ct.includes('employment')) {
                            hoverLabel = subData.company_name || check.subcheck_company || '';
                          } else if (ct.includes('education')) {
                            hoverLabel = subData.university_name || subData.institution_name || check.subcheck_institution || '';
                          }
                          const tooltipText = hoverLabel ? `${ct.includes('employment') ? 'Company' : 'University'}: ${hoverLabel}` : 'View details';
                          return (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => openCheckDetails(check)}
                              title={tooltipText}
                              data-testid={`view-check-tooltip-${check.check_id || check.id}`}
                              className="h-5 w-fit p-0 text-blue-500 hover:text-blue-700 text-[10px]"
                            >
                              <Eye className="w-3 h-3 mr-1" />View
                            </Button>
                          );
                        })()}
                      </div>
                    </TableCell>
                    <TableCell>
                      {(check.mode_of_verification || check.initiated_mode) ? (
                        <div className="flex items-center gap-1"><ModeIcon className="w-3 h-3 text-gray-500" /><span className="text-xs capitalize">{(check.mode_of_verification || check.initiated_mode).replace('_', ' ')}</span></div>
                      ) : (check.assigned_vendor?.vendor_name || check.assigned_vendor_name || check.vendor_name) ? (
                        <div className="flex items-center gap-1"><Truck className="w-3 h-3 text-indigo-500" /><span className="text-xs text-indigo-600">Via Vendor</span></div>
                      ) : <span className="text-xs text-gray-400 italic">-</span>}
                    </TableCell>
                    <TableCell>
                      {/* Verification Status with Email Reply Indicator */}
                      <div className="flex flex-col gap-1">
                        <Badge data-testid={`check-status-badge-${check.check_id || idx}`} className={`text-xs ${
                          check.verification_progress === 'completed' || check.verification_progress === 'verified' ? 'bg-green-100 text-green-800' :
                          check.verification_progress === 'in_progress' ? 'bg-blue-100 text-blue-800' :
                          check.verification_progress === 'initiated' ? 'bg-green-100 text-green-800' :
                          'bg-orange-100 text-orange-800'
                        }`}>
                          {check.verification_progress ? check.verification_progress.split('_').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ') : 'Not Initiated'}
                        </Badge>
                        {/* Insufficiency-cleared sub-label so agents differentiate fresh In Progress
                            cases from cases where QC just cleared an insufficiency */}
                        {check.verification_progress === 'in_progress' && check.insufficiency_cleared_date && !check.has_insufficiency && (
                          <span data-testid={`check-insuff-cleared-${check.check_id || idx}`} className="text-[10px] font-semibold text-amber-700">
                            Insuff cleared
                          </span>
                        )}
                        {/* Show verification result below completed badge */}
                        {(check.verification_progress === 'completed' || check.verification_progress === 'verified') && shouldShowResult(check.verification_result) && (
                          <span data-testid={`check-result-label-${check.check_id || idx}`} className={`text-[10px] font-semibold ${getResultTextColor(check.verification_result)}`}>
                            {getResultLabel(check.verification_result)}
                          </span>
                        )}
                        {/* Email Reply Indicator - shows when HR/University has replied */}
                        {check.email_reply_received && (
                          <div className="flex items-center gap-1 text-[10px] text-emerald-600 bg-emerald-50 px-1.5 py-0.5 rounded cursor-pointer hover:bg-emerald-100" 
                               title={check.reply_preview || 'Reply received - click to view'}
                               onClick={() => openCheckDetails(check)}>
                            <Mail className="w-3 h-3" />
                            <span className="truncate max-w-[80px]">
                              {check.reply_preview ? check.reply_preview.slice(0, 25) + (check.reply_preview.length > 25 ? '...' : '') : 'Reply Received'}
                            </span>
                          </div>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      {/* Vendor Name */}
                      {(check.assigned_vendor?.vendor_name || check.assigned_vendor_name || check.vendor_name || (typeof check.assigned_vendor === 'string' && check.assigned_vendor)) ? (
                        <Badge variant="outline" className="text-xs bg-indigo-50 text-indigo-700">
                          {check.assigned_vendor?.vendor_name || check.assigned_vendor_name || check.vendor_name || check.assigned_vendor}
                        </Badge>
                      ) : <span className="text-xs text-gray-400">-</span>}
                    </TableCell>
                    <TableCell className="text-xs">
                      {/* Vendor Remarks - FE Residence Confirmed Status */}
                      {(() => {
                        const residenceStatus = check.field_verification?.residence_confirmed || 
                                                check.address_verification?.field_residence_confirmed;
                        if (!residenceStatus) return <span className="text-gray-400">-</span>;
                        
                        const statusConfig = {
                          'yes': { label: 'Yes', suffix: 'Positive', className: 'bg-green-100 text-green-700' },
                          'no': { label: 'No', suffix: 'Negative', className: 'bg-red-100 text-red-700' },
                          'shifted': { label: 'Shifted', suffix: 'Pending', className: 'bg-amber-100 text-amber-700' },
                          'not_traceable': { label: 'Not Traceable', suffix: 'Insuff', className: 'bg-orange-100 text-orange-700' },
                          'left_job_not_cooperating': { label: 'Left Job/Not Co-operating', suffix: 'Insuff', className: 'bg-orange-100 text-orange-700' },
                          'incorrect_number_switchoff': { label: 'Incorrect Number/Switch Off', suffix: 'Insuff', className: 'bg-orange-100 text-orange-700' }
                        };
                        
                        const config = statusConfig[residenceStatus] || { label: residenceStatus, suffix: '', className: 'bg-gray-100 text-gray-700' };
                        
                        return (
                          <div className="flex flex-col gap-0.5">
                            <Badge className={`text-[10px] ${config.className}`}>
                              {config.label}
                            </Badge>
                            {config.suffix && (
                              <span className={`text-[9px] font-semibold ${
                                config.suffix === 'Positive' ? 'text-green-600' : 
                                config.suffix === 'Negative' ? 'text-red-600' : 
                                'text-orange-600'
                              }`}>
                                {config.suffix}
                              </span>
                            )}
                          </div>
                        );
                      })()}
                    </TableCell>
                    <TableCell className="text-xs text-center text-gray-500">{check.initiation_email_sent_at ? formatDate(check.initiation_email_sent_at) : '-'}</TableCell>
                    <TableCell className="text-xs text-center">
                      <div className={assignmentInfo.color}>{formatDate(assignmentInfo.date)}</div>
                      {assignmentInfo.date && <div className="text-[9px] text-gray-400">{assignmentInfo.label}</div>}
                    </TableCell>
                    <TableCell className="text-xs text-center text-gray-500">{formatDate(check.completed_at)}</TableCell>
                    <TableCell className="text-center">
                      <Badge className={`text-xs ${check.is_within_tat ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>{check.days_elapsed || 0}/{check.agent_tat || 7}</Badge>
                    </TableCell>
                    {showActions && (
                      <TableCell>
                        <div className="flex items-center gap-1 flex-wrap">
                          {/* Initiate/Resend button — always visible for email-based checks */}
                          {['employment_check', 'education_check', 'reference_check'].includes(check.check_type) ? (
                            check.initiation_email_sent ? (
                              <Button variant="outline" size="sm" onClick={() => openAutoInitiate(check)} className="text-xs h-7 px-2 text-blue-600 hover:bg-blue-50 border-blue-200" title="Resend Verification Email"><RefreshCw className="w-3 h-3 mr-1" />Resend</Button>
                            ) : (
                              <Button variant="outline" size="sm" onClick={() => openAutoInitiate(check)} className="text-xs h-7 px-2 text-green-600 hover:bg-green-50 border-green-200" title="Initiate Verification"><Send className="w-3 h-3 mr-1" />Initiate</Button>
                            )
                          ) : (!check.verification_progress || check.verification_progress === 'not_initiated') && (
                            <Button variant="outline" size="sm" onClick={() => openAutoInitiate(check)} className="text-xs h-7 px-2 text-green-600 hover:bg-green-50 border-green-200" title="Initiate Verification"><Send className="w-3 h-3 mr-1" />Initiate</Button>
                          )}
                          <Button variant="outline" size="sm" onClick={() => openUpdateStatus(check)} className="text-xs h-7 px-2"><Play className="w-3 h-3 mr-1" />Update</Button>
                          {/* View Field Verification Form - for address/criminal/ID checks */}
                          {['address_check', 'criminal_check', 'criminal_court_check', 'criminal_advocate_check', 'id_verification', 'id_check', 'court_record_check'].includes(check.check_type) && (
                            <Button 
                              variant="outline" 
                              size="sm" 
                              onClick={() => openFieldVerificationView(check)} 
                              className={`text-xs h-7 px-2 ${check.field_verification_submitted || check.field_verification_status === 'completed' ? 'text-emerald-600 hover:bg-emerald-50 border-emerald-200' : 'text-indigo-600 hover:bg-indigo-50 border-indigo-200'}`}
                              title="View Field Verification Details"
                            >
                              <FileEdit className="w-3 h-3 mr-1" />
                              {check.field_verification_submitted || check.field_verification_status === 'completed' ? 'View Form' : 'Field Form'}
                            </Button>
                          )}
                          {(check.status === 'verified' || check.status === 'clear' || check.status === 'failed') && (
                            <Button variant="outline" size="sm" onClick={() => openGenerateReport(check)} className="text-xs h-7 px-2 border-green-200 text-green-700"><FileText className="w-3 h-3" /></Button>
                          )}
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild><Button variant="ghost" size="sm" className="h-7 w-7 p-0"><MoreVertical className="w-4 h-4" /></Button></DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              <DropdownMenuItem onClick={() => openRaiseInsuff(check)} className="text-red-600 cursor-pointer"><AlertTriangle className="w-4 h-4 mr-2" />Raise Insuff</DropdownMenuItem>
                              <DropdownMenuItem onClick={() => { setExtraCostCheck(check); setShowExtraCostDialog(true); }} className="text-amber-600 cursor-pointer" data-testid="raise-extra-cost-btn"><CreditCard className="w-4 h-4 mr-2" />Raise Extra Cost</DropdownMenuItem>
                              <DropdownMenuSeparator />
                              <DropdownMenuItem onClick={() => openReturnDialog(check)} className="text-amber-600 cursor-pointer"><RotateCcw className="w-4 h-4 mr-2" />Return to Ops Manager</DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </div>
                      </TableCell>
                    )}
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>
      </CardContent>
    </Card>
  );
  };

  const renderContent = () => {
    switch (activeTab) {
      case 'dashboard': return renderDashboard();
      case 'my-checks':
        return (
          <div className="space-y-4">
            <Card className="border-2 border-blue-100">
              <CardContent className="pt-4">
                {/* Filter dropdowns row */}
                <div className="grid grid-cols-1 md:grid-cols-5 gap-3 mb-4">
                  <Select value={filters.clientId} onValueChange={(v) => setFilters({...filters, clientId: v})}>
                    <SelectTrigger><SelectValue placeholder="Client" /></SelectTrigger>
                    <SelectContent><SelectItem value="all">All Clients</SelectItem>{clients.map(c => (<SelectItem key={c.id} value={c.id}>{c.company_name || c.name}</SelectItem>))}</SelectContent>
                  </Select>
                  <Select value={filters.checkType} onValueChange={(v) => setFilters({...filters, checkType: v})}>
                    <SelectTrigger><SelectValue placeholder="Check Type" /></SelectTrigger>
                    <SelectContent><SelectItem value="all">All Types</SelectItem>{CHECK_TYPES.map(ct => (<SelectItem key={ct.value} value={ct.value}>{ct.label}</SelectItem>))}</SelectContent>
                  </Select>
                  <Select value={filters.status} onValueChange={(v) => setFilters({...filters, status: v})}>
                    <SelectTrigger><SelectValue placeholder="Verification Status" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Status</SelectItem>
                      <SelectItem value="not_initiated">Not Initiated</SelectItem>
                      <SelectItem value="in_progress">In Progress</SelectItem>
                      <SelectItem value="completed">Completed</SelectItem>
                    </SelectContent>
                  </Select>
                  <Select value={filters.tatStatus} onValueChange={(v) => setFilters({...filters, tatStatus: v})}>
                    <SelectTrigger><SelectValue placeholder="TAT Status" /></SelectTrigger>
                    <SelectContent><SelectItem value="all">All TAT</SelectItem><SelectItem value="within_tat">Within TAT</SelectItem><SelectItem value="over_tat">Over TAT</SelectItem></SelectContent>
                  </Select>
                  <Select value={filters.modeOfVerification} onValueChange={(v) => setFilters({...filters, modeOfVerification: v})}>
                    <SelectTrigger><SelectValue placeholder="Mode" /></SelectTrigger>
                    <SelectContent><SelectItem value="all">All Modes</SelectItem>{MODE_OF_VERIFICATION.map(m => (<SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>))}</SelectContent>
                  </Select>
                </div>
                {/* Search, Apply, Clear - Centered */}
                <div className="flex items-center justify-center gap-4 py-3 border-t border-gray-100">
                  <div className="relative w-64">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <Input placeholder="Search by name, email, case ID..." className="pl-10" value={filters.search} onChange={(e) => setFilters({...filters, search: e.target.value})} onKeyDown={(e) => e.key === 'Enter' && fetchData()} />
                  </div>
                  <Button onClick={fetchData} className="bg-blue-600 hover:bg-blue-700 text-white px-6"><Filter className="w-4 h-4 mr-2" />Apply Filters</Button>
                  <Button variant="outline" onClick={() => { setFilters({ clientId: 'all', checkType: 'all', status: 'all', tatStatus: 'all', modeOfVerification: 'all', dateFilterType: 'latest', assignedFrom: '', assignedTo: '', search: '' }); fetchData(); }} className="border-red-300 text-red-600 hover:bg-red-50 px-6"><RotateCcw className="w-4 h-4 mr-2" />Clear All</Button>
                </div>
              </CardContent>
            </Card>
            {renderChecksTable(myChecks, 'My Assigned Checks', true)}
          </div>
        );
      case 'all-checks':
        return (
          <div className="space-y-4">
            <Card className="border-2 border-blue-100">
              <CardContent className="pt-4">
                {/* Filter dropdowns row */}
                <div className="grid grid-cols-1 md:grid-cols-5 gap-3 mb-4">
                  <Select value={filters.clientId} onValueChange={(v) => setFilters({...filters, clientId: v})}>
                    <SelectTrigger><SelectValue placeholder="Client" /></SelectTrigger>
                    <SelectContent><SelectItem value="all">All Clients</SelectItem>{clients.map(c => (<SelectItem key={c.id} value={c.id}>{c.company_name || c.name}</SelectItem>))}</SelectContent>
                  </Select>
                  <Select value={filters.checkType} onValueChange={(v) => setFilters({...filters, checkType: v})}>
                    <SelectTrigger><SelectValue placeholder="Check Type" /></SelectTrigger>
                    <SelectContent><SelectItem value="all">All Types</SelectItem>{CHECK_TYPES.map(ct => (<SelectItem key={ct.value} value={ct.value}>{ct.label}</SelectItem>))}</SelectContent>
                  </Select>
                  <Select value={filters.status} onValueChange={(v) => setFilters({...filters, status: v})}>
                    <SelectTrigger><SelectValue placeholder="Verification Status" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Status</SelectItem>
                      <SelectItem value="not_initiated">Not Initiated</SelectItem>
                      <SelectItem value="in_progress">In Progress</SelectItem>
                      <SelectItem value="completed">Completed</SelectItem>
                    </SelectContent>
                  </Select>
                  <Select value={filters.tatStatus} onValueChange={(v) => setFilters({...filters, tatStatus: v})}>
                    <SelectTrigger><SelectValue placeholder="TAT Status" /></SelectTrigger>
                    <SelectContent><SelectItem value="all">All TAT</SelectItem><SelectItem value="within_tat">Within TAT</SelectItem><SelectItem value="over_tat">Over TAT</SelectItem></SelectContent>
                  </Select>
                  <Select value={filters.modeOfVerification} onValueChange={(v) => setFilters({...filters, modeOfVerification: v})}>
                    <SelectTrigger><SelectValue placeholder="Mode" /></SelectTrigger>
                    <SelectContent><SelectItem value="all">All Modes</SelectItem>{MODE_OF_VERIFICATION.map(m => (<SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>))}</SelectContent>
                  </Select>
                </div>
                {/* Search, Apply, Clear - Centered */}
                <div className="flex items-center justify-center gap-4 py-3 border-t border-gray-100">
                  <div className="relative w-64">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <Input placeholder="Search by name, email, case ID..." className="pl-10" value={filters.search} onChange={(e) => setFilters({...filters, search: e.target.value})} onKeyDown={(e) => e.key === 'Enter' && fetchData()} />
                  </div>
                  <Button onClick={fetchData} className="bg-blue-600 hover:bg-blue-700 text-white px-6"><Filter className="w-4 h-4 mr-2" />Apply Filters</Button>
                  <Button variant="outline" onClick={() => { setFilters({ clientId: 'all', checkType: 'all', status: 'all', tatStatus: 'all', modeOfVerification: 'all', dateFilterType: 'latest', assignedFrom: '', assignedTo: '', search: '' }); fetchData(); }} className="border-red-300 text-red-600 hover:bg-red-50 px-6"><RotateCcw className="w-4 h-4 mr-2" />Clear All</Button>
                </div>
              </CardContent>
            </Card>
            {renderChecksTable(checks, 'All Checks', true)}
          </div>
        );
      case 'completed': return renderChecksTable(completedChecks, 'Completed Checks', true);
      case 'client-tat': return renderClientTatSummary();
      case 'companies': return <CompaniesTab />;
      case 'institutions': return <InstitutionsTab />;
      case 'reports': return <TeamVendorReports user={user} reportType="internal" />;
      case 'employment-verification':
        return employmentVerification && (
          <EmploymentVerificationForm caseId={employmentVerification.caseId} checkId={employmentVerification.checkId} checkData={employmentVerification.checkData}
            employeeData={employeeData} documents={documents} onBack={() => setActiveTab('my-checks')} onComplete={() => { setActiveTab('my-checks'); fetchData(); }} />
        );
      case 'education-verification':
        return educationVerification && (
          <EducationVerificationForm caseId={educationVerification.caseId} checkId={educationVerification.checkId} checkData={educationVerification.checkData}
            employeeData={employeeData} documents={documents} onBack={() => setActiveTab('my-checks')} onComplete={() => { setActiveTab('my-checks'); fetchData(); }} />
        );
      case 'address-verification':
        return addressVerification && (
          <AddressVerificationForm caseId={addressVerification.caseId} checkId={addressVerification.checkId} checkData={addressVerification.checkData}
            employeeData={employeeData} documents={documents} clientName={addressVerification.clientName} entityName={addressVerification.entityName}
            onBack={() => setActiveTab('my-checks')} onComplete={() => { setActiveTab('my-checks'); fetchData(); }} />
        );
      case 'criminal-verification':
        return criminalVerification && (
          <CourtCriminalVerificationForm caseId={criminalVerification.caseId} checkId={criminalVerification.checkId} checkData={criminalVerification.checkData}
            employeeData={employeeData} documents={documents} onBack={() => setActiveTab('my-checks')} onComplete={() => { setActiveTab('my-checks'); fetchData(); }} />
        );
      case 'id-verification':
        return idVerification && (
          <IDVerificationForm caseId={idVerification.caseId} checkId={idVerification.checkId} checkData={idVerification.checkData}
            employeeData={employeeData} documents={documents} onBack={() => setActiveTab('my-checks')} onComplete={() => { setActiveTab('my-checks'); fetchData(); }} />
        );
      case 'reference-check':
        return referenceCheck && (
          <ReferenceCheckForm caseId={referenceCheck.caseId} checkId={referenceCheck.checkId} checkData={referenceCheck.checkData}
            employeeData={employeeData} documents={documents} onBack={() => setActiveTab('my-checks')} onComplete={() => { setActiveTab('my-checks'); fetchData(); }} />
        );
      case 'drug-test':
        return drugTest && (
          <DrugTestForm caseId={drugTest.caseId} checkId={drugTest.checkId} checkData={drugTest.checkData}
            employeeData={employeeData} documents={documents} onBack={() => setActiveTab('my-checks')} onComplete={() => { setActiveTab('my-checks'); fetchData(); }} />
        );
      default: return renderDashboard();
    }
  };

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Top Header - Matching Operations Manager Theme */}
      <div className="bg-gradient-to-r from-indigo-700 via-purple-700 to-blue-700 text-white px-6 py-4 flex items-center justify-between shadow-lg">
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-3">
            <div className="bg-white/20 p-2 rounded-lg">
              <Users className="w-6 h-6" />
            </div>
            <div>
              <h1 className="text-xl font-bold">
                {user?.role === 'vendor_management' || user?.team_type === 'vendor_management' || user?.team_type === 'vendor' 
                  ? 'Agent Vendor Team' 
                  : 'Agent Internal Team'}
              </h1>
              <p className="text-xs text-white/70">
                {user?.role === 'vendor_management' || user?.team_type === 'vendor_management' || user?.team_type === 'vendor'
                  ? 'Address, Criminal, ID & External Verifications'
                  : 'Employment, Education & Reference Verifications'}
              </p>
            </div>
          </div>
        </div>
        <div className="flex items-center gap-4">
          {/* User Info */}
          <div className="flex items-center gap-3 bg-white/10 rounded-lg px-4 py-2">
            <div className="bg-white/20 p-2 rounded-full">
              <User className="w-5 h-5" />
            </div>
            <div className="text-right">
              <p className="text-sm font-semibold">{user?.name || 'Agent'}</p>
              <p className="text-xs text-white/70">{user?.email || ''}</p>
            </div>
          </div>
          <div className="bg-white/10 rounded-full p-1"><NotificationCenter /></div>
          {/* Export In-Progress cases (Agent Internal Team only) */}
          {!(user?.role === 'vendor_management' || user?.team_type === 'vendor_management' || user?.team_type === 'vendor') && (
            <Button
              variant="ghost"
              size="sm"
              className="text-white hover:bg-white/20 bg-blue-600/40"
              onClick={handleExportInProgress}
              disabled={exportInProgressLoading}
              data-testid="export-in-progress-btn"
              title="Download an Excel of your Not-Initiated + In-Progress Education and Employment/Reference cases"
            >
              {exportInProgressLoading ? (
                <><RefreshCw className="w-4 h-4 mr-2 animate-spin" />Exporting...</>
              ) : (
                <><Download className="w-4 h-4 mr-2" />Export Excel</>
              )}
            </Button>
          )}
          <Button variant="ghost" size="sm" className="text-white hover:bg-white/20" onClick={fetchData}><RefreshCw className="w-4 h-4 mr-2" />Refresh</Button>
          <Button variant="ghost" size="sm" className="text-white hover:bg-white/20 border border-white/30" onClick={onLogout}><LogOut className="w-4 h-4 mr-2" />Logout</Button>
        </div>
      </div>

      {/* Tabs Navigation - 2 rows with distinct colors */}
      <div className="bg-gradient-to-r from-slate-50 to-slate-100 border-b px-6 py-3 shadow-sm">
        <div className="flex gap-2 flex-wrap">
          {menuItems.map(item => {
            const Icon = item.icon;
            const isActive = activeTab === item.id;
            return (
              <button key={item.id} onClick={() => setActiveTab(item.id)}
                className={`px-4 py-2 flex items-center gap-2 text-sm font-semibold rounded-lg transition-all duration-200 border ${
                  isActive 
                    ? `${item.bgColor} ${item.textColor} shadow-md border-${item.color}-300 ring-2 ring-offset-1 ring-${item.color}-300`
                    : `bg-white text-gray-600 border-gray-200 hover:${item.bgColor} hover:${item.textColor}`
                }`}
                data-testid={`tab-${item.id}`}>
                <Icon className={`w-4 h-4 ${isActive ? item.textColor : 'text-gray-400'}`} /><span>{item.label}</span>
              </button>
            );
          })}
        </div>
      </div>

      {/* Main Content */}
      <main className="p-6">{renderContent()}</main>

      {/* Verification Update Dialog - Comprehensive version */}
      {/* Feb 2026 — Agent Internal Team can edit completed checks (all
          types) so they can fix wrong photos/annexures/details and
          re-submit without needing to reopen the case. */}
      <VerificationUpdateDialog
        open={showUpdateStatus}
        onOpenChange={setShowUpdateStatus}
        check={selectedCheck}
        onSuccess={fetchData}
        allowEditWhenCompleted={true}
      />

      {/* Generate Report Dialog */}
      <Dialog open={showGenerateReport} onOpenChange={setShowGenerateReport}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle>Generate Verification Report</DialogTitle></DialogHeader>
          <div className="space-y-4 py-4">
            {reportData && selectedCheck && (
              <div className="p-4 bg-slate-50 rounded-lg space-y-2 text-sm">
                <div className="flex justify-between"><span className="text-slate-500">Check Type:</span><span className="font-medium">{reportData.check_type}</span></div>
                <div className="flex justify-between"><span className="text-slate-500">Employee:</span><span className="font-medium">{reportData.employee_name}</span></div>
                <div className="flex justify-between"><span className="text-slate-500">Status:</span><Badge className={STATUS_COLORS[reportData.status]}>{reportData.status}</Badge></div>
                <div className="flex justify-between"><span className="text-slate-500">Mode:</span><span className="font-medium capitalize">{reportData.mode_of_verification?.replace('_', ' ') || 'N/A'}</span></div>
                <div className="flex justify-between"><span className="text-slate-500">TAT:</span><span className="font-medium">{selectedCheck.days_elapsed || 0} days (Limit: {selectedCheck.agent_tat || 7})</span></div>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowGenerateReport(false)}>Cancel</Button>
            <Button onClick={generateReport} className="bg-green-600 hover:bg-green-700"><FileText className="w-4 h-4 mr-2" />Generate PDF</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Return to Ops Manager Dialog */}
      <Dialog open={showReturnDialog} onOpenChange={setShowReturnDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle className="flex items-center gap-2 text-amber-700"><RotateCcw className="w-5 h-5" />Return Check to Operations Manager</DialogTitle></DialogHeader>
          <div className="space-y-4 py-4">
            {selectedCheck && (
              <div className="p-4 bg-amber-50 rounded-lg border border-amber-200">
                <div className="grid grid-cols-2 gap-2 text-sm">
                  <div><span className="text-amber-700">Case ID:</span> {selectedCheck.case_id}</div>
                  <div><span className="text-amber-700">Check ID:</span> {selectedCheck.check_id || selectedCheck.id}</div>
                  <div><span className="text-amber-700">Type:</span> {getCheckTypeLabel(selectedCheck.check_type)}</div>
                  <div><span className="text-amber-700">Employee:</span> {selectedCheck.employee_name}</div>
                </div>
              </div>
            )}
            <div><Label>Reason for Return *</Label>
              <Select value={returnData.reason} onValueChange={(v) => setReturnData({...returnData, reason: v})}>
                <SelectTrigger><SelectValue placeholder="Select reason" /></SelectTrigger>
                <SelectContent>{RETURN_REASONS.map(r => (<SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>))}</SelectContent>
              </Select>
            </div>
            <div><Label>Additional Notes</Label><Textarea value={returnData.notes} onChange={(e) => setReturnData({...returnData, notes: e.target.value})} placeholder="Provide details..." rows={3} /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowReturnDialog(false)}>Cancel</Button>
            <Button onClick={handleReturnCheck} className="bg-amber-600 hover:bg-amber-700"><RotateCcw className="w-4 h-4 mr-2" />Return Check</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Check Details Dialog with Document Preview */}
      <Dialog open={showCheckDetails} onOpenChange={(open) => { if (!open) { setShowCheckDetails(false); setPreviewDoc(null); setCheckDocuments([]); setCheckDetailData(null); setSelectedSubcheck(null); } }}>
        <DialogContent className="max-w-7xl max-h-[95vh] overflow-hidden">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><Eye className="w-5 h-5" />Check Details & Document Review</DialogTitle></DialogHeader>
          {selectedCheck && (
            <div className="flex gap-4" style={{ height: 'calc(95vh - 100px)' }}>
              {/* Left Side: Check Information & Subchecks */}
              <div className={`${previewDoc ? 'w-1/2' : 'w-full'} overflow-auto space-y-3 pr-2`}>
                {/* Basic Info Row */}
                <div className="grid grid-cols-3 gap-3">
                  <div className="p-3 bg-slate-50 rounded-lg">
                    <h4 className="font-semibold mb-2 text-xs text-slate-600">Case Info</h4>
                    <div className="space-y-1 text-xs">
                      <div><span className="text-gray-500">Case:</span> <span className="font-mono">{selectedCheck.case_id}</span></div>
                      <div><span className="text-gray-500">Check:</span> <span className="font-mono text-blue-600">{selectedCheck.check_id || selectedCheck.id}</span></div>
                      <div><Badge variant="outline" className="text-xs">{selectedCheck.check_type?.replace(/_/g, ' ')}</Badge></div>
                      <div><Badge className={STATUS_COLORS[selectedCheck.status] + ' text-xs'}>{selectedCheck.status?.replace('_', ' ')}</Badge></div>
                    </div>
                  </div>
                  <div className="p-3 bg-slate-50 rounded-lg">
                    <h4 className="font-semibold mb-2 text-xs text-slate-600">Employee</h4>
                    <div className="space-y-1 text-xs">
                      <div className="font-medium">{selectedCheck.employee_name || checkDetailData?.employee?.name}</div>
                      <div className="text-gray-500">{selectedCheck.employee_email}</div>
                      {(checkDetailData?.employee?.dob || selectedCheck.dob) && (
                        <div className="text-gray-500">DOB: {checkDetailData?.employee?.dob || selectedCheck.dob}</div>
                      )}
                      <div className="text-gray-500">{selectedCheck.phone || checkDetailData?.employee?.phone || '-'}</div>
                      {(selectedCheck.alternate_contact || checkDetailData?.employee?.alternate_contact) && (
                        <div className="text-gray-500">Alt: {selectedCheck.alternate_contact || checkDetailData?.employee?.alternate_contact}</div>
                      )}
                      <div className="text-gray-500">{selectedCheck.client_name}</div>
                    </div>
                  </div>
                  <div className="p-3 bg-slate-50 rounded-lg">
                    <h4 className="font-semibold mb-2 text-xs text-slate-600">TAT</h4>
                    <div className="space-y-1 text-xs">
                      <div><Badge className={selectedCheck.is_within_tat ? 'bg-green-100 text-green-800 text-xs' : 'bg-red-100 text-red-800 text-xs'}>{selectedCheck.days_elapsed || 0}/{selectedCheck.agent_tat || 7}d</Badge></div>
                      <div><span className="text-gray-500">Assigned:</span> {formatDate(selectedCheck.assigned_at)}</div>
                    </div>
                  </div>
                </div>

                {/* Sub-Check Data Section - Using data from selectedCheck directly */}
                {selectedCheck.has_subcheck && selectedCheck.subcheck_data && (
                  <div className="p-3 bg-green-50 rounded-lg border border-green-200">
                    <h4 className="font-semibold mb-2 text-sm text-green-800">
                      {selectedCheck.subcheck_type || selectedCheck.check_type?.replace(/_/g, ' ')} Details
                    </h4>
                    <div className="grid grid-cols-2 gap-3 text-xs">
                      {/* Address Check Details (also used for criminal court/advocate checks) */}
                      {(selectedCheck.check_type === 'address_check' || selectedCheck.check_type === 'criminal_court_check' || selectedCheck.check_type === 'criminal_advocate_check') && (
                        <>
                          <div className="col-span-2">
                            <span className="text-gray-500">Full Address:</span>
                            <p className="font-medium mt-1">{selectedCheck.subcheck_data.complete_address || selectedCheck.subcheck_data.address_line1 || selectedCheck.subcheck_location || '-'}</p>
                          </div>
                          <div>
                            <span className="text-gray-500">City:</span>
                            <span className="ml-1 font-medium">{selectedCheck.subcheck_data.city || '-'}</span>
                          </div>
                          <div>
                            <span className="text-gray-500">State:</span>
                            <span className="ml-1 font-medium">{selectedCheck.subcheck_data.state || '-'}</span>
                          </div>
                          <div>
                            <span className="text-gray-500">Pincode:</span>
                            <span className="ml-1 font-medium">{selectedCheck.subcheck_data.pincode || '-'}</span>
                          </div>
                          <div>
                            <span className="text-gray-500">Address Type:</span>
                            <span className="ml-1 font-medium">{selectedCheck.subcheck_data.address_type || selectedCheck.subcheck_type || '-'}</span>
                          </div>
                          {selectedCheck.subcheck_data.landmark && (
                            <div className="col-span-2">
                              <span className="text-gray-500">Landmark:</span>
                              <span className="ml-1 font-medium">{selectedCheck.subcheck_data.landmark}</span>
                            </div>
                          )}
                          {selectedCheck.subcheck_data.address_proof_url && (
                            <Button variant="outline" size="sm" className="col-span-2 text-xs" onClick={() => window.open(selectedCheck.subcheck_data.address_proof_url, '_blank')}>
                              <FileText className="w-3 h-3 mr-1" />View Proof
                            </Button>
                          )}
                        </>
                      )}
                      
                      {/* Education Check Details */}
                      {selectedCheck.check_type === 'education_check' && (
                        <>
                          <div>
                            <span className="text-gray-500">University/Institution:</span>
                            <p className="font-medium">{selectedCheck.subcheck_data.university_name || selectedCheck.subcheck_data.institution_name || selectedCheck.subcheck_institution || '-'}</p>
                          </div>
                          <div>
                            <span className="text-gray-500">College:</span>
                            <span className="ml-1 font-medium">{selectedCheck.subcheck_data.college || '-'}</span>
                          </div>
                          <div>
                            <span className="text-gray-500">Course/Degree:</span>
                            <span className="ml-1 font-medium">{selectedCheck.subcheck_data.course || selectedCheck.subcheck_data.degree || selectedCheck.subcheck_detail || '-'}</span>
                          </div>
                          <div>
                            <span className="text-gray-500">Year of Passing:</span>
                            <span className="ml-1 font-medium">{selectedCheck.subcheck_data.year_of_passing || '-'}</span>
                          </div>
                          <div>
                            <span className="text-gray-500">Roll No:</span>
                            <span className="ml-1 font-medium">{selectedCheck.subcheck_data.roll_number || '-'}</span>
                          </div>
                          <div>
                            <span className="text-gray-500">Grade/CGPA:</span>
                            <span className="ml-1 font-medium">{selectedCheck.subcheck_data.grade || selectedCheck.subcheck_data.cgpa || '-'}</span>
                          </div>
                          <div className="col-span-2 flex gap-1 flex-wrap">
                            {selectedCheck.subcheck_data.degree_certificate_url && <Button variant="outline" size="sm" className="text-xs" onClick={() => window.open(selectedCheck.subcheck_data.degree_certificate_url, '_blank')}><FileText className="w-3 h-3 mr-1" />Degree</Button>}
                            {selectedCheck.subcheck_data.marks_card_url && <Button variant="outline" size="sm" className="text-xs" onClick={() => window.open(selectedCheck.subcheck_data.marks_card_url, '_blank')}><FileText className="w-3 h-3 mr-1" />Marks</Button>}
                          </div>
                        </>
                      )}
                      
                      {/* Employment Check Details */}
                      {selectedCheck.check_type === 'employment_check' && (
                        <>
                          <div className="col-span-2">
                            <span className="text-gray-500">Company:</span>
                            <p className="font-medium">{selectedCheck.subcheck_data.company_name || selectedCheck.subcheck_company || '-'}</p>
                          </div>
                          <div>
                            <span className="text-gray-500">Designation:</span>
                            <span className="ml-1 font-medium">{selectedCheck.subcheck_data.designation || '-'}</span>
                          </div>
                          <div>
                            <span className="text-gray-500">Employee ID:</span>
                            <span className="ml-1 font-medium">{selectedCheck.subcheck_data.employee_id || '-'}</span>
                          </div>
                          <div>
                            <span className="text-gray-500">From:</span>
                            <span className="ml-1 font-medium">{formatDateDDMonYYYY(selectedCheck.subcheck_data.employed_from || selectedCheck.subcheck_data.start_date) || '-'}</span>
                          </div>
                          <div>
                            <span className="text-gray-500">To:</span>
                            <span className="ml-1 font-medium">{(selectedCheck.subcheck_data.employed_to || selectedCheck.subcheck_data.end_date) ? formatDateDDMonYYYY(selectedCheck.subcheck_data.employed_to || selectedCheck.subcheck_data.end_date) : 'Present'}</span>
                          </div>
                          <div>
                            <span className="text-gray-500">RM:</span>
                            <span className="ml-1 font-medium">{selectedCheck.subcheck_data.rm_name || '-'}</span>
                          </div>
                          {(selectedCheck.subcheck_data.rm_contact_number || selectedCheck.subcheck_data.rm_contact || selectedCheck.subcheck_data.rm_phone) && (
                            <div>
                              <span className="text-gray-500">RM Contact:</span>
                              <span className="ml-1 font-medium">{selectedCheck.subcheck_data.rm_contact_number || selectedCheck.subcheck_data.rm_contact || selectedCheck.subcheck_data.rm_phone}</span>
                            </div>
                          )}
                          <div>
                            <span className="text-gray-500">RM Email:</span>
                            <span className="ml-1 font-medium text-blue-600">{selectedCheck.subcheck_rm_email || selectedCheck.subcheck_data.rm_email || '-'}</span>
                          </div>
                          {(selectedCheck.subcheck_data.hr_name) && (
                            <div>
                              <span className="text-gray-500">HR Name:</span>
                              <span className="ml-1 font-medium">{selectedCheck.subcheck_data.hr_name}</span>
                            </div>
                          )}
                          {(selectedCheck.subcheck_data.hr_contact_number || selectedCheck.subcheck_data.hr_contact || selectedCheck.subcheck_data.hr_phone) && (
                            <div>
                              <span className="text-gray-500">HR Contact:</span>
                              <span className="ml-1 font-medium">{selectedCheck.subcheck_data.hr_contact_number || selectedCheck.subcheck_data.hr_contact || selectedCheck.subcheck_data.hr_phone}</span>
                            </div>
                          )}
                          <div>
                            <span className="text-gray-500">HR Email:</span>
                            <span className="ml-1 font-medium text-blue-600">{selectedCheck.subcheck_hr_email || selectedCheck.subcheck_data.hr_email || '-'}</span>
                          </div>
                          {(selectedCheck.subcheck_data.salary_per_month || selectedCheck.subcheck_data.ctc || selectedCheck.subcheck_data.salary) && (
                            <div>
                              <span className="text-gray-500">CTC:</span>
                              <span className="ml-1 font-medium">{selectedCheck.subcheck_data.salary_per_month || selectedCheck.subcheck_data.ctc || selectedCheck.subcheck_data.salary}</span>
                            </div>
                          )}
                          <div className="col-span-2 flex gap-1 flex-wrap">
                            {selectedCheck.subcheck_data.relieving_letter_url && <Button variant="outline" size="sm" className="text-xs" onClick={() => window.open(selectedCheck.subcheck_data.relieving_letter_url, '_blank')}><FileText className="w-3 h-3 mr-1" />Relieving</Button>}
                            {selectedCheck.subcheck_data.latest_payslip_url && <Button variant="outline" size="sm" className="text-xs" onClick={() => window.open(selectedCheck.subcheck_data.latest_payslip_url, '_blank')}><FileText className="w-3 h-3 mr-1" />Payslip</Button>}
                          </div>
                        </>
                      )}
                      
                      {/* Reference Check Details */}
                      {(selectedCheck.check_type === 'reference_check' || selectedCheck.check_type === 'personal_reference' || selectedCheck.check_type === 'professional_reference') && (
                        <>
                          <div>
                            <span className="text-gray-500">Reference Name:</span>
                            <span className="ml-1 font-medium">{selectedCheck.subcheck_data.name || selectedCheck.subcheck_name || '-'}</span>
                          </div>
                          <div>
                            <span className="text-gray-500">Phone:</span>
                            <span className="ml-1 font-medium">{selectedCheck.subcheck_data.phone || selectedCheck.subcheck_detail || '-'}</span>
                          </div>
                          <div>
                            <span className="text-gray-500">Email:</span>
                            <span className="ml-1 font-medium">{selectedCheck.subcheck_data.email || '-'}</span>
                          </div>
                          <div>
                            <span className="text-gray-500">Relationship:</span>
                            <span className="ml-1 font-medium">{selectedCheck.subcheck_data.relationship || '-'}</span>
                          </div>
                        </>
                      )}
                      
                      {/* ID/Document Check Details */}
                      {selectedCheck.check_type === 'id_check' && (
                        <>
                          <div>
                            <span className="text-gray-500">ID Type:</span>
                            <span className="ml-1 font-medium">{selectedCheck.subcheck_id_type || selectedCheck.subcheck_data.document_type || '-'}</span>
                          </div>
                          <div>
                            <span className="text-gray-500">ID Number:</span>
                            <span className="ml-1 font-medium">{selectedCheck.subcheck_id_number || selectedCheck.subcheck_data.id_number || '-'}</span>
                          </div>
                        </>
                      )}
                      
                      {/* Criminal Check Details */}
                      {(selectedCheck.check_type === 'criminal_court_check' || selectedCheck.check_type === 'court_advocate_check') && (
                        <>
                          <div className="col-span-2">
                            <span className="text-gray-500">Location:</span>
                            <span className="ml-1 font-medium">{selectedCheck.subcheck_location || selectedCheck.subcheck_data.location || '-'}</span>
                          </div>
                        </>
                      )}
                      
                      {/* Generic fallback - show all subcheck_data fields */}
                      {!['address_check', 'education_check', 'employment_check', 'reference_check', 'personal_reference', 'professional_reference', 'id_check', 'criminal_court_check', 'court_advocate_check'].includes(selectedCheck.check_type) && (
                        <>
                          {Object.entries(selectedCheck.subcheck_data).filter(([key, value]) => 
                            value && !['id', 'status', 'type', 'created_at', 'updated_at'].includes(key)
                          ).map(([key, value]) => (
                            <div key={key}>
                              <span className="text-gray-500">{key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}:</span>
                              <span className="ml-1 font-medium">{typeof value === 'object' ? JSON.stringify(value) : String(value)}</span>
                            </div>
                          ))}
                        </>
                      )}
                    </div>
                  </div>
                )}
                
                {/* Quick Info Section for checks without subcheck_data but with subcheck fields */}
                {selectedCheck.has_subcheck && !selectedCheck.subcheck_data && (selectedCheck.subcheck_location || selectedCheck.subcheck_institution || selectedCheck.subcheck_company || selectedCheck.subcheck_name) && (
                  <div className="p-3 bg-green-50 rounded-lg border border-green-200">
                    <h4 className="font-semibold mb-2 text-sm text-green-800">
                      {selectedCheck.subcheck_type || selectedCheck.check_type?.replace(/_/g, ' ')} Details
                    </h4>
                    <div className="grid grid-cols-2 gap-2 text-xs">
                      {selectedCheck.subcheck_location && (
                        <div className="col-span-2">
                          <span className="text-gray-500">Location:</span>
                          <span className="ml-1 font-medium">{selectedCheck.subcheck_location}</span>
                        </div>
                      )}
                      {selectedCheck.subcheck_institution && (
                        <div>
                          <span className="text-gray-500">Institution:</span>
                          <span className="ml-1 font-medium">{selectedCheck.subcheck_institution}</span>
                        </div>
                      )}
                      {selectedCheck.subcheck_company && (
                        <div>
                          <span className="text-gray-500">Company:</span>
                          <span className="ml-1 font-medium">{selectedCheck.subcheck_company}</span>
                        </div>
                      )}
                      {selectedCheck.subcheck_name && (
                        <div>
                          <span className="text-gray-500">Name:</span>
                          <span className="ml-1 font-medium">{selectedCheck.subcheck_name}</span>
                        </div>
                      )}
                      {selectedCheck.subcheck_detail && (
                        <div>
                          <span className="text-gray-500">Detail:</span>
                          <span className="ml-1 font-medium">{selectedCheck.subcheck_detail}</span>
                        </div>
                      )}
                      {selectedCheck.subcheck_hr_email && (
                        <div>
                          <span className="text-gray-500">HR Email:</span>
                          <span className="ml-1 font-medium text-blue-600">{selectedCheck.subcheck_hr_email}</span>
                        </div>
                      )}
                      {selectedCheck.subcheck_rm_email && (
                        <div>
                          <span className="text-gray-500">RM Email:</span>
                          <span className="ml-1 font-medium text-blue-600">{selectedCheck.subcheck_rm_email}</span>
                        </div>
                      )}
                    </div>
                  </div>
                )}
                
                {/* Fallback to API-fetched data if no local subcheck data */}
                {!selectedCheck.has_subcheck && checkDetailData?.employee && (() => {
                  const subchecks = getSubchecks(selectedCheck.check_type, checkDetailData.employee);
                  if (subchecks.length === 0) return null;
                  return (
                    <div className="p-3 bg-green-50 rounded-lg border border-green-200">
                      <h4 className="font-semibold mb-2 text-sm text-green-800">{selectedCheck.check_type?.replace(/_/g, ' ')} Data</h4>
                      <div className="space-y-2">
                        {subchecks.map((subcheck, idx) => (
                          <div key={idx} className="p-2 bg-white rounded border cursor-pointer hover:bg-green-100" onClick={() => setSelectedSubcheck(idx)}>
                            <div className="flex justify-between items-center">
                              <span className="font-medium text-sm">{subcheck.type}</span>
                              <Eye className="w-4 h-4 text-gray-400" />
                            </div>
                            {(selectedCheck.check_type === 'address_check' || selectedCheck.check_type === 'criminal_court_check' || selectedCheck.check_type === 'criminal_advocate_check') && <p className="text-xs text-gray-500 truncate">{subcheck.complete_address}, {subcheck.city}</p>}
                            {selectedCheck.check_type === 'education_check' && <p className="text-xs text-gray-500 truncate">{subcheck.university_name} - {subcheck.course}</p>}
                            {selectedCheck.check_type === 'employment_check' && <p className="text-xs text-gray-500 truncate">{subcheck.company_name} - {subcheck.designation}</p>}
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                })()}

                {/* Documents List */}
                <div className="p-3 bg-blue-50 rounded-lg border border-blue-200">
                  <h4 className="font-semibold mb-2 text-sm text-blue-800">Documents ({checkDocuments.length})</h4>
                  {loadingDocs ? (
                    <div className="flex items-center justify-center py-4"><RefreshCw className="w-5 h-5 animate-spin text-blue-500" /></div>
                  ) : checkDocuments.length === 0 ? (
                    <p className="text-sm text-slate-500 text-center py-2">No documents</p>
                  ) : (
                    <div className="grid grid-cols-2 gap-2">
                      {checkDocuments.map((doc, idx) => (
                        <div key={doc.id || idx} className={`flex items-center justify-between p-2 rounded-md cursor-pointer text-xs ${previewDoc?.id === doc.id ? 'bg-blue-200 border-2 border-blue-400' : doc.document_type === 'declaration_form' ? 'bg-indigo-50 hover:bg-indigo-100 border border-indigo-200' : 'bg-white hover:bg-blue-100 border'}`}
                          onClick={() => setPreviewDoc(doc)}>
                          <span className="truncate">{doc.document_type === 'declaration_form' ? 'Declaration Form' : doc.document_type || `Doc ${idx + 1}`}</span>
                          <Eye className="w-3 h-3 text-gray-400" />
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              {/* Right Side: Document Preview */}
              {previewDoc && (
                <div className="w-1/2 border-l pl-4 flex flex-col">
                  <div className="flex justify-between items-center mb-2 pb-2 border-b">
                    <div>
                      <h4 className="font-semibold">Document Preview</h4>
                      <p className="text-xs text-slate-500">{previewDoc.document_type || previewDoc.original_filename}</p>
                    </div>
                    <div className="flex gap-1">
                      <Button variant="outline" size="sm" onClick={() => window.open(previewDoc.file_url, '_blank')}><ExternalLink className="w-4 h-4" /></Button>
                      <Button variant="ghost" size="sm" onClick={() => setPreviewDoc(null)}><X className="w-4 h-4" /></Button>
                    </div>
                  </div>
                  {checkDocuments.length > 1 && (
                    <div className="flex flex-wrap gap-1 mb-2">
                      {checkDocuments.map((doc, idx) => (
                        <Button key={idx} variant={previewDoc?.id === doc.id ? "default" : "outline"} size="sm" onClick={() => setPreviewDoc(doc)} className="text-xs h-7">
                          {doc.document_type || `Doc ${idx + 1}`}
                        </Button>
                      ))}
                    </div>
                  )}
                  <div className="flex-1 bg-slate-100 rounded-lg overflow-hidden" style={{ minHeight: '400px' }}>
                    {previewDoc.file_url ? (
                      previewDoc.file_url?.includes('.pdf') ? (
                        <iframe src={previewDoc.file_url} className="w-full h-full" style={{ minHeight: '500px' }} title="Preview" />
                      ) : (
                        <div className="h-full flex items-center justify-center p-4">
                          <img src={previewDoc.file_url} alt="Document" className="max-w-full max-h-full object-contain rounded shadow-lg" />
                        </div>
                      )
                    ) : (
                      <div className="h-full flex items-center justify-center text-slate-400"><FileText className="w-16 h-16" /></div>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Drill-down Dialog */}
      <Dialog open={showDrillDown} onOpenChange={setShowDrillDown}>
        <DialogContent className="max-w-5xl max-h-[80vh] overflow-auto">
          <DialogHeader><DialogTitle className="flex items-center gap-2">{getDrillDownData().title}<Badge>{getDrillDownData().data.length}</Badge></DialogTitle></DialogHeader>
          <div className="py-4">
            <Table>
              <TableHeader>
                <TableRow className="bg-gradient-to-r from-indigo-600 to-blue-600">
                  <TableHead className="text-white font-semibold">Case ID</TableHead><TableHead className="text-white font-semibold">Check ID</TableHead><TableHead className="text-white font-semibold">Employee</TableHead><TableHead className="text-white font-semibold">Client</TableHead><TableHead className="text-white font-semibold">Check Type</TableHead><TableHead className="text-white font-semibold">Status</TableHead><TableHead className="text-white font-semibold">Assigned</TableHead><TableHead className="text-white font-semibold">TAT</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {getDrillDownData().data.map((check, idx) => (
                  <TableRow key={idx}>
                    <TableCell className="font-mono text-xs">{check.case_id}</TableCell>
                    <TableCell className="font-mono text-xs text-blue-600">{check.check_id?.slice(-8) || check.id?.slice(-8)}</TableCell>
                    <TableCell className="text-sm">{check.employee_name}</TableCell>
                    <TableCell className="text-sm">{check.client_name || '-'}</TableCell>
                    <TableCell><Badge variant="outline" className="text-xs">{getCheckTypeLabel(check.check_type)}</Badge></TableCell>
                    <TableCell>
                      <div className="flex flex-col gap-0.5">
                        <Badge className={`${STATUS_COLORS[check.status] || 'bg-gray-100'} text-xs`}>{check.status?.replace('_', ' ')}</Badge>
                        {check.status === 'in_progress' && check.insufficiency_cleared_date && !check.has_insufficiency && (
                          <span className="text-[10px] font-semibold text-amber-700">Insuff cleared</span>
                        )}
                        {(check.status === 'completed' || check.status === 'verified') && shouldShowResult(check.verification_result) && (
                          <span className={`text-[10px] font-semibold ${getResultTextColor(check.verification_result)}`}>{getResultLabel(check.verification_result)}</span>
                        )}
                      </div>
                    </TableCell>
                    <TableCell className="text-xs">{formatDate(check.assigned_at)}</TableCell>
                    <TableCell><Badge className={`text-xs ${check.is_within_tat ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>{check.days_elapsed || 0}/{check.agent_tat || 7}</Badge></TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </DialogContent>
      </Dialog>

      {/* Auto-Initiate Dialog */}
      <Dialog open={showAutoInitiateDialog} onOpenChange={setShowAutoInitiateDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Send className="w-5 h-5 text-green-600" />
              Auto-Initiate Verification
            </DialogTitle>
          </DialogHeader>
          
          {autoInitiateLoading ? (
            <div className="py-8 text-center">
              <RefreshCw className="w-8 h-8 animate-spin mx-auto mb-4 text-blue-500" />
              <p className="text-slate-600">Checking auto-initiate eligibility...</p>
            </div>
          ) : autoInitiateResults ? (
            <div className="space-y-4">
              {/* Check Info */}
              <div className="bg-slate-50 rounded-lg p-4">
                <div className="grid grid-cols-2 gap-2 text-sm">
                  <div>
                    <span className="text-slate-500">Check Type:</span>
                    <p className="font-medium">{getCheckTypeLabel(selectedCheck?.check_type)}</p>
                  </div>
                  <div>
                    <span className="text-slate-500">Candidate:</span>
                    <p className="font-medium">{selectedCheck?.employee_name}</p>
                  </div>
                  <div>
                    <span className="text-slate-500">Case ID:</span>
                    <p className="font-medium">{selectedCheck?.case_id}</p>
                  </div>
                </div>
              </div>

              {/* Auto-Initiate Result */}
              {autoInitiateResults.can_auto_initiate ? (
                <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                  <div className="flex items-center gap-2 mb-3">
                    <CheckCircle className="w-5 h-5 text-green-600" />
                    <span className="font-semibold text-green-800">Auto-Initiate Available</span>
                  </div>
                  
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-slate-600">Target Type:</span>
                      <Badge className="bg-blue-100 text-blue-800">
                        {autoInitiateResults.target_type === 'hr_email' ? 'HR Email' : 
                         autoInitiateResults.target_type === 'institution' ? 'Institution' :
                         autoInitiateResults.target_type}
                      </Badge>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-slate-600">Email:</span>
                      <span className="font-medium text-green-700">{autoInitiateResults.target_email}</span>
                    </div>
                    {autoInitiateResults.target_name && (
                      <div className="flex justify-between">
                        <span className="text-slate-600">Recipient:</span>
                        <span className="font-medium">{autoInitiateResults.target_name}</span>
                      </div>
                    )}
                  </div>

                  {/* Sub-check Details */}
                  {autoInitiateResults.subcheck_detail && (
                    <div className="mt-3 pt-3 border-t border-green-200">
                      <p className="text-xs text-slate-500 mb-2">Verification Details:</p>
                      <div className="grid grid-cols-2 gap-1 text-xs">
                        {Object.entries(autoInitiateResults.subcheck_detail).map(([key, value]) => 
                          value && (
                            <div key={key}>
                              <span className="text-slate-500">{key.replace(/_/g, ' ')}:</span>
                              <span className="ml-1 font-medium">{value}</span>
                            </div>
                          )
                        )}
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <AlertTriangle className="w-5 h-5 text-amber-600" />
                    <span className="font-semibold text-amber-800">Auto-Initiate Not Available</span>
                  </div>
                  <p className="text-sm text-amber-700">{autoInitiateResults.message}</p>
                </div>
              )}
            </div>
          ) : null}

          <DialogFooter className="mt-4">
            <Button variant="outline" onClick={() => setShowAutoInitiateDialog(false)}>
              Cancel
            </Button>
            {autoInitiateResults?.can_auto_initiate && (
              <Button 
                onClick={handleAutoInitiateEmail}
                disabled={autoInitiateLoading}
                className="bg-green-600 hover:bg-green-700"
              >
                {autoInitiateLoading ? (
                  <RefreshCw className="w-4 h-4 animate-spin mr-2" />
                ) : (
                  <Send className="w-4 h-4 mr-2" />
                )}
                Send Verification Email
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Enhanced Email Initiation Dialog */}
      <InitiateVerificationEmailDialog
        open={showInitiateEmailDialog}
        onOpenChange={setShowInitiateEmailDialog}
        check={selectedCheck}
        onSuccess={() => {
          setShowInitiateEmailDialog(false);
          setSelectedCheck(null);
          fetchData();
          toast.success('Verification email sent successfully!');
        }}
      />

      {/* Bulk Initiate Dialog */}
      <Dialog open={showBulkInitiateDialog} onOpenChange={(open) => { 
        setShowBulkInitiateDialog(open); 
        if (!open) { 
          setBulkInitiateVendorId(''); 
          setBulkInitiateAutoAssign(true);
          setBulkInitiateSpecialInstructions('');
        } 
      }}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-green-700">
              <Send className="w-5 h-5" />Bulk Initiate & Assign Verification
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="bg-green-50 p-4 rounded-lg">
              <p className="text-sm text-green-800">
                You are about to process <span className="font-bold">{selectedChecks.length}</span> selected check(s).
              </p>
            </div>
            
            {/* Auto-Assign Toggle */}
            <div className="space-y-3">
              <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                <div>
                  <p className="text-sm font-medium">Auto-Assign Mode</p>
                  <p className="text-xs text-gray-500">Automatically assign based on system rules</p>
                </div>
                <div className="flex items-center gap-2">
                  <span className={`text-xs ${bulkInitiateAutoAssign ? 'text-green-600' : 'text-gray-400'}`}>
                    {bulkInitiateAutoAssign ? 'ON' : 'OFF'}
                  </span>
                  <Checkbox 
                    checked={bulkInitiateAutoAssign} 
                    onCheckedChange={setBulkInitiateAutoAssign}
                    className="data-[state=checked]:bg-green-600"
                  />
                </div>
              </div>
              
              {/* Vendor Selection (shown when auto-assign is OFF) */}
              {!bulkInitiateAutoAssign && (
                <div className="p-3 bg-blue-50 rounded-lg border border-blue-200 space-y-2">
                  <p className="text-sm font-medium text-blue-800 flex items-center gap-2">
                    <Truck className="w-4 h-4" />
                    Select Vendor to Assign Checks
                  </p>
                  <Select value={bulkInitiateVendorId} onValueChange={setBulkInitiateVendorId}>
                    <SelectTrigger className="bg-white">
                      <SelectValue placeholder="Choose a vendor..." />
                    </SelectTrigger>
                    <SelectContent>
                      {vendors.map(v => (
                        <SelectItem key={v.id} value={v.id}>
                          <div className="flex items-center gap-2">
                            <span>{v.vendor_name || v.name}</span>
                            {v.city && <span className="text-xs text-gray-500">({v.city})</span>}
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-blue-600">
                    All selected checks will be assigned to this vendor for verification.
                  </p>
                </div>
              )}
              
              {/* Special Instructions (shown when assigning to vendor) */}
              {!bulkInitiateAutoAssign && (
                <div className="p-3 bg-amber-50 rounded-lg border border-amber-200 space-y-2">
                  <p className="text-sm font-medium text-amber-800">Special Instructions to Vendor</p>
                  <Textarea
                    placeholder="Enter any special instructions for the vendor (e.g., 'Find additional phone number', 'Verify alternate address')"
                    value={bulkInitiateSpecialInstructions}
                    onChange={(e) => setBulkInitiateSpecialInstructions(e.target.value)}
                    className="bg-white min-h-[80px]"
                  />
                  <p className="text-xs text-amber-600">
                    These instructions will be visible to the vendor and field agent
                  </p>
                </div>
              )}
            </div>
            
            <div className="text-xs text-gray-500 bg-gray-50 p-3 rounded">
              {bulkInitiateAutoAssign 
                ? 'This will mark the checks as "In Progress" and send notifications to relevant parties.'
                : 'This will assign all selected checks to the chosen vendor and mark them as "Vendor Assigned".'
              }
            </div>
            
            <div className="flex gap-2 justify-end">
              <Button variant="outline" onClick={() => setShowBulkInitiateDialog(false)} disabled={bulkInitiateLoading}>Cancel</Button>
              <Button 
                className="bg-green-600 hover:bg-green-700" 
                onClick={handleBulkInitiate} 
                disabled={bulkInitiateLoading || (!bulkInitiateAutoAssign && !bulkInitiateVendorId)}
              >
                {bulkInitiateLoading ? (
                  <><RefreshCw className="w-4 h-4 mr-2 animate-spin" />Processing...</>
                ) : (
                  <><Send className="w-4 h-4 mr-2" />{bulkInitiateAutoAssign ? 'Initiate' : 'Assign to Vendor'} ({selectedChecks.length})</>
                )}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Raise Insufficiency Dialog */}
      <Dialog open={showRaiseInsuff} onOpenChange={setShowRaiseInsuff}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-red-600 flex items-center gap-2">
              <AlertTriangle className="w-5 h-5" />Raise Insufficiency
            </DialogTitle>
          </DialogHeader>
          {selectedCheck && (
            <div className="space-y-4">
              <div className="bg-gray-50 p-3 rounded-lg space-y-1">
                <p className="text-sm"><span className="text-gray-500">Employee:</span> <span className="font-medium">{selectedCheck.employee_name}</span></p>
                <p className="text-sm"><span className="text-gray-500">Check Type:</span> <Badge variant="outline">{getCheckTypeLabel(selectedCheck.check_type)}</Badge></p>
                <p className="text-sm"><span className="text-gray-500">Case ID:</span> <span className="font-mono text-xs">{selectedCheck.case_id}</span></p>
              </div>
              <div className="space-y-2">
                <Label>Insufficiency Reason *</Label>
                <Select value={insuffData.reason} onValueChange={(v) => setInsuffData({...insuffData, reason: v})}>
                  <SelectTrigger><SelectValue placeholder="Select reason" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="missing_documents">Missing Documents</SelectItem>
                    <SelectItem value="incorrect_information">Incorrect Information</SelectItem>
                    <SelectItem value="unverifiable_details">Unverifiable Details</SelectItem>
                    <SelectItem value="contact_not_reachable">Contact Not Reachable</SelectItem>
                    <SelectItem value="address_mismatch">Address Mismatch</SelectItem>
                    <SelectItem value="company_not_found">Company/Institution Not Found</SelectItem>
                    <SelectItem value="other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Additional Details</Label>
                <Textarea 
                  placeholder="Provide additional details about the insufficiency..."
                  value={insuffData.details}
                  onChange={(e) => setInsuffData({...insuffData, details: e.target.value})}
                  rows={3}
                />
              </div>
              <div className="flex gap-2 justify-end">
                <Button variant="outline" onClick={() => setShowRaiseInsuff(false)}>Cancel</Button>
                <Button className="bg-red-600 hover:bg-red-700" onClick={handleRaiseInsuff}>Raise Insufficiency</Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
      
      {/* Extra Cost Request Dialog */}
      <Dialog open={showExtraCostDialog} onOpenChange={(open) => { setShowExtraCostDialog(open); if (!open) { setExtraCostAmount(''); setExtraCostRemarks(''); setExtraCostCheck(null); } }}>
        <DialogContent className="max-w-md" data-testid="extra-cost-dialog">
          <DialogHeader><DialogTitle className="text-amber-600 flex items-center gap-2"><CreditCard className="w-5 h-5" />Raise Extra Cost Request</DialogTitle></DialogHeader>
          {extraCostCheck && (
            <div className="space-y-4">
              <div className="bg-gray-50 p-3 rounded-lg space-y-1">
                <p className="text-sm"><span className="text-gray-500">Employee:</span> <span className="font-medium">{extraCostCheck.employee_name}</span></p>
                <p className="text-sm"><span className="text-gray-500">Check:</span> <Badge variant="outline">{extraCostCheck.check_type?.replace(/_/g,' ')}</Badge></p>
                <p className="text-sm"><span className="text-gray-500">Case:</span> <span className="font-mono text-xs">{extraCostCheck.case_id}</span></p>
              </div>
              <div className="space-y-2">
                <Label>Amount (Rs.) *</Label>
                <Input type="number" placeholder="Enter extra cost amount" value={extraCostAmount} onChange={(e) => setExtraCostAmount(e.target.value)} data-testid="extra-cost-amount" />
              </div>
              <div className="space-y-2">
                <Label>Reason / Remarks *</Label>
                <Textarea placeholder="Explain why extra cost is needed..." value={extraCostRemarks} onChange={(e) => setExtraCostRemarks(e.target.value)} rows={3} data-testid="extra-cost-remarks" />
              </div>
              <div className="flex gap-2 justify-end">
                <Button variant="outline" onClick={() => setShowExtraCostDialog(false)}>Cancel</Button>
                <Button className="bg-amber-600 hover:bg-amber-700 text-white" onClick={handleRaiseExtraCost} disabled={!extraCostAmount || !extraCostRemarks || extraCostLoading} data-testid="extra-cost-submit">
                  {extraCostLoading ? 'Submitting...' : 'Submit Request'}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Bulk Document Upload Dialog */}

      {/* Transfer to Vendor Team Dialog */}
      <Dialog open={showTransferDialog} onOpenChange={(open) => {
        setShowTransferDialog(open);
        if (!open) { setTransferAgentId(''); setTransferReason(''); }
      }}>
        <DialogContent className="max-w-lg" data-testid="transfer-to-vendor-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-purple-700">
              <Truck className="w-5 h-5" />Transfer to Vendor Team Agent
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="bg-purple-50 p-4 rounded-lg">
              <p className="text-sm text-purple-800">
                Transfer <span className="font-bold">{selectedChecks.length}</span> selected check(s) to an internal vendor team agent.
              </p>
              <p className="text-xs text-purple-600 mt-1">This bypasses the Ops Manager and assigns checks directly to the selected team member.</p>
            </div>

            <div className="space-y-2">
              <Label className="text-sm font-medium">Select Vendor Team Agent *</Label>
              <Select value={transferAgentId} onValueChange={setTransferAgentId}>
                <SelectTrigger data-testid="transfer-vendor-select"><SelectValue placeholder="Choose an agent..." /></SelectTrigger>
                <SelectContent>
                  {vendorTeamAgents.map(a => (
                    <SelectItem key={a.id} value={a.id}>
                      <div className="flex items-center gap-2">
                        <span>{a.name}</span>
                        <span className="text-xs text-gray-500">({a.email})</span>
                        {a.assigned_check_types?.length > 0 && (
                          <span className="text-xs text-purple-600 ml-1">[{a.assigned_check_types.join(', ')}]</span>
                        )}
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label className="text-sm font-medium">Reason / Instructions</Label>
              <Textarea
                placeholder="Enter reason for transfer or special instructions..."
                value={transferReason}
                onChange={(e) => setTransferReason(e.target.value)}
                className="min-h-[80px] text-sm"
                data-testid="transfer-reason-input"
              />
            </div>

            <div className="flex gap-2 justify-end">
              <Button variant="outline" onClick={() => setShowTransferDialog(false)} disabled={transferLoading}>Cancel</Button>
              <Button
                className="bg-purple-600 hover:bg-purple-700 text-white"
                onClick={handleTransferToVendor}
                disabled={transferLoading || !transferAgentId}
                data-testid="transfer-confirm-btn"
              >
                {transferLoading ? (
                  <><RefreshCw className="w-4 h-4 mr-2 animate-spin" />Transferring...</>
                ) : (
                  <><Truck className="w-4 h-4 mr-2" />Transfer ({selectedChecks.length})</>
                )}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={showBulkUpload} onOpenChange={setShowBulkUpload}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-auto">
          <DialogHeader>
            <DialogTitle>Bulk Document Upload</DialogTitle>
          </DialogHeader>
          <BulkDocumentUpload onClose={() => { setShowBulkUpload(false); fetchData(); }} />
        </DialogContent>
      </Dialog>

      {/* Bulk Reminder Result Dialog */}
      <Dialog open={!!showBulkReminderResult} onOpenChange={() => setShowBulkReminderResult(null)}>
        <DialogContent className="sm:max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="bulk-reminder-result-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Mail className="w-5 h-5 text-amber-600" />
              Bulk Reminder Results
            </DialogTitle>
          </DialogHeader>
          {showBulkReminderResult && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <div className="bg-green-50 rounded-lg p-3 text-center">
                  <p className="text-2xl font-bold text-green-700">{showBulkReminderResult.sent}</p>
                  <p className="text-xs text-green-600">Reminders Sent</p>
                </div>
                <div className="bg-amber-50 rounded-lg p-3 text-center">
                  <p className="text-2xl font-bold text-amber-700">{showBulkReminderResult.skipped_recent}</p>
                  <p className="text-xs text-amber-600">Skipped ({'<'} 2 days)</p>
                </div>
                <div className="bg-gray-50 rounded-lg p-3 text-center">
                  <p className="text-2xl font-bold text-gray-700">{showBulkReminderResult.skipped_no_recipient}</p>
                  <p className="text-xs text-gray-500">No Recipient</p>
                </div>
                <div className="bg-red-50 rounded-lg p-3 text-center">
                  <p className="text-2xl font-bold text-red-700">{showBulkReminderResult.failed}</p>
                  <p className="text-xs text-red-600">Failed</p>
                </div>
              </div>
              {showBulkReminderResult.details?.length > 0 && (
                <div className="max-h-64 overflow-auto border rounded-lg">
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-gray-50">
                        <TableHead className="text-xs whitespace-nowrap">Case ID</TableHead>
                        <TableHead className="text-xs whitespace-nowrap">Type</TableHead>
                        <TableHead className="text-xs">Candidate</TableHead>
                        <TableHead className="text-xs whitespace-nowrap text-right">Status</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {showBulkReminderResult.details.map((d, i) => (
                        <TableRow key={i}>
                          <TableCell className="text-xs whitespace-nowrap">{d.case_id}</TableCell>
                          <TableCell className="text-xs whitespace-nowrap">{d.check_type?.replace('_check', '')}</TableCell>
                          <TableCell className="text-xs break-all">{d.candidate || d.recipient || '-'}</TableCell>
                          <TableCell className="text-xs whitespace-nowrap text-right">
                            <Badge variant="outline" className={`whitespace-nowrap ${
                              d.status === 'sent' ? 'bg-green-50 text-green-700' :
                              d.status === 'skipped_recent' ? 'bg-amber-50 text-amber-700' :
                              d.status === 'failed' ? 'bg-red-50 text-red-700' : 'bg-gray-50 text-gray-600'
                            }`}>
                              {d.status === 'sent' ? 'Sent' : d.status === 'skipped_recent' ? '< 2 days' : d.status === 'skipped_no_recipient' ? 'No email' : 'Failed'}
                            </Badge>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
              <DialogFooter>
                <Button onClick={() => setShowBulkReminderResult(null)} variant="outline" data-testid="bulk-reminder-close-btn">Close</Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Field Verification View Dialog */}
      <Dialog open={showFieldVerificationView} onOpenChange={setShowFieldVerificationView}>
        <DialogContent className="sm:max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileEdit className="w-5 h-5 text-indigo-600" />
              View Updated Form - Field Verification Details
            </DialogTitle>
          </DialogHeader>
          {fieldVerificationData && (
            <div className="space-y-4 py-2">
              {/* Case Info */}
              <div className="bg-slate-50 p-4 rounded-lg">
                <h4 className="font-medium text-sm mb-2 flex items-center gap-2">
                  <FileText className="w-4 h-4" />
                  Case Information
                </h4>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
                  <div><span className="text-gray-500">Case ID:</span> <span className="font-medium">{fieldVerificationData.case_id}</span></div>
                  <div><span className="text-gray-500">Check ID:</span> <span className="font-medium">{fieldVerificationData.check_id || fieldVerificationData.id}</span></div>
                  <div><span className="text-gray-500">Employee:</span> <span className="font-medium">{fieldVerificationData.employee_name}</span></div>
                  <div><span className="text-gray-500">Check Type:</span> <span className="font-medium capitalize">{fieldVerificationData.check_type?.replace(/_/g, ' ')}</span></div>
                  <div><span className="text-gray-500">Client:</span> <span className="font-medium">{fieldVerificationData.client_name || '-'}</span></div>
                  <div><span className="text-gray-500">Status:</span> <Badge className={fieldVerificationData.field_verification?.submitted ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'}>{fieldVerificationData.field_verification?.submitted ? 'Submitted' : 'Pending'}</Badge></div>
                  <div><span className="text-gray-500">Submitted At:</span> <span className="font-medium">{fieldVerificationData.field_verification?.submitted_at ? new Date(fieldVerificationData.field_verification.submitted_at).toLocaleString() : '-'}</span></div>
                </div>
              </div>

              {/* Address Details - For Address Checks */}
              {fieldVerificationData.check_type?.toLowerCase().includes('address') && (
                <div className="bg-indigo-50 p-4 rounded-lg border border-indigo-200">
                  <h4 className="font-medium text-sm mb-3 text-indigo-800 flex items-center gap-2">
                    <MapPin className="w-4 h-4" />
                    Address Details
                    <Badge className={
                      fieldVerificationData.subcheck_data?.address_type?.toLowerCase().includes('present') ||
                      fieldVerificationData.address_type?.toLowerCase().includes('present') || 
                      fieldVerificationData.subcheck_type?.toLowerCase().includes('present') ||
                      fieldVerificationData.check_index === 1
                        ? 'bg-blue-100 text-blue-700 ml-2' 
                        : 'bg-purple-100 text-purple-700 ml-2'
                    }>
                      {fieldVerificationData.subcheck_data?.address_type?.toLowerCase().includes('present') ||
                       fieldVerificationData.address_type?.toLowerCase().includes('present') || 
                       fieldVerificationData.subcheck_type?.toLowerCase().includes('present') ||
                       fieldVerificationData.check_index === 1
                        ? 'Present Address' 
                        : fieldVerificationData.subcheck_data?.address_type?.toLowerCase().includes('permanent') ||
                          fieldVerificationData.address_type?.toLowerCase().includes('permanent') ||
                          fieldVerificationData.subcheck_type?.toLowerCase().includes('permanent') ||
                          fieldVerificationData.check_index === 2
                          ? 'Permanent Address'
                          : `Address ${fieldVerificationData.check_index || ''}`}
                    </Badge>
                  </h4>
                  <div className="bg-white p-3 rounded border border-indigo-100">
                    <p className="text-sm font-medium text-gray-800">
                      {/* Use subcheck_data which has the address from case initiation */}
                      {fieldVerificationData.subcheck_data?.complete_address ||
                       fieldVerificationData.subcheck_data?.address_line1 ||
                       fieldVerificationData.subcheck_location ||
                       fieldVerificationData.verified_details?.complete_address ||
                       [
                        fieldVerificationData.subcheck_data?.address_line1,
                        fieldVerificationData.subcheck_data?.address_line2,
                        fieldVerificationData.subcheck_data?.landmark,
                        fieldVerificationData.subcheck_data?.city,
                        fieldVerificationData.subcheck_data?.state,
                        fieldVerificationData.subcheck_data?.pincode
                       ].filter(Boolean).join(', ') || 
                       'Address details not available'}
                    </p>
                    {fieldVerificationData.subcheck_data && (
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mt-2 text-xs">
                        {fieldVerificationData.subcheck_data.city && (
                          <div><span className="text-gray-500">City:</span> <span className="font-medium">{fieldVerificationData.subcheck_data.city}</span></div>
                        )}
                        {fieldVerificationData.subcheck_data.state && (
                          <div><span className="text-gray-500">State:</span> <span className="font-medium">{fieldVerificationData.subcheck_data.state}</span></div>
                        )}
                        {fieldVerificationData.subcheck_data.pincode && (
                          <div><span className="text-gray-500">Pincode:</span> <span className="font-medium">{fieldVerificationData.subcheck_data.pincode}</span></div>
                        )}
                        {fieldVerificationData.subcheck_data.landmark && (
                          <div><span className="text-gray-500">Landmark:</span> <span className="font-medium">{fieldVerificationData.subcheck_data.landmark}</span></div>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Field Agent Info */}
              <div className="bg-purple-50 p-4 rounded-lg border border-purple-200">
                <h4 className="font-medium text-sm mb-3 text-purple-800 flex items-center gap-2">
                  <User className="w-4 h-4" />
                  Field Agent Details
                </h4>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                  <div>
                    <p className="text-purple-600 text-xs mb-1">Verifier Name</p>
                    <p className="font-medium">{fieldVerificationData.field_agent_name || fieldVerificationData.field_verification?.verifier_name || fieldVerificationData.field_verification?.person_met_name || '-'}</p>
                  </div>
                  <div>
                    <p className="text-purple-600 text-xs mb-1">Contact Number</p>
                    <p className="font-medium">{fieldVerificationData.field_agent_phone || fieldVerificationData.field_verification?.verifier_contact || fieldVerificationData.field_verification?.person_met_contact || '-'}</p>
                  </div>
                  <div>
                    <p className="text-purple-600 text-xs mb-1">Date of Visit</p>
                    <p className="font-medium">
                      {fieldVerificationData.field_verification?.date_of_visit || 
                       (fieldVerificationData.field_verification?.submitted_at ? new Date(fieldVerificationData.field_verification.submitted_at).toLocaleDateString() : '-')}
                    </p>
                  </div>
                  <div>
                    <p className="text-purple-600 text-xs mb-1">Time of Visit</p>
                    <p className="font-medium">
                      {fieldVerificationData.field_verification?.time_of_visit || 
                       (fieldVerificationData.field_verification?.submitted_at ? new Date(fieldVerificationData.field_verification.submitted_at).toLocaleTimeString() : '-')}
                    </p>
                  </div>
                </div>
              </div>

              {/* GPS & Location */}
              {fieldVerificationData.field_verification?.gps_coordinates && (
                <div className="bg-emerald-50 p-4 rounded-lg border border-emerald-200">
                  <h4 className="font-medium text-sm mb-2 text-emerald-800 flex items-center gap-2">
                    <MapPin className="w-4 h-4" />
                    GPS Location
                  </h4>
                  <div className="flex items-center gap-4">
                    <span className="text-sm font-mono bg-white px-3 py-1 rounded border">{fieldVerificationData.field_verification.gps_coordinates}</span>
                    <a 
                      href={`https://maps.google.com/?q=${fieldVerificationData.field_verification.gps_coordinates}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-emerald-700 text-sm underline flex items-center gap-1"
                    >
                      <ExternalLink className="w-3 h-3" /> View on Map
                    </a>
                  </div>
                </div>
              )}

              {/* Verifier Details */}
              {fieldVerificationData.field_verification && (
                <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                  <h4 className="font-medium text-sm mb-3 text-blue-800 flex items-center gap-2">
                    <CheckCircle className="w-4 h-4" />
                    Verifier Details
                  </h4>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                    <div>
                      <p className="text-blue-600 text-xs mb-1">Residence Confirmed</p>
                      <Badge className={fieldVerificationData.field_verification.residence_confirmed === 'yes' ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'}>
                        {fieldVerificationData.field_verification.residence_confirmed || '-'}
                      </Badge>
                    </div>
                    <div>
                      <p className="text-blue-600 text-xs mb-1">Type of Residence</p>
                      <p className="font-medium capitalize">{fieldVerificationData.field_verification.residence_type?.replace(/_/g, ' ') || fieldVerificationData.field_verification.residence_status || '-'}</p>
                    </div>
                    <div>
                      <p className="text-blue-600 text-xs mb-1">House/Building Type</p>
                      <p className="font-medium capitalize">{fieldVerificationData.field_verification.building_type?.replace(/_/g, ' ') || '-'}</p>
                    </div>
                    <div>
                      <p className="text-blue-600 text-xs mb-1">Locality Type</p>
                      <p className="font-medium capitalize">{fieldVerificationData.field_verification.locality_type?.replace(/_/g, ' ') || '-'}</p>
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm mt-3 pt-3 border-t border-blue-200">
                    <div>
                      <p className="text-blue-600 text-xs mb-1">Period of Stay From</p>
                      <p className="font-medium">{fieldVerificationData.field_verification.period_of_stay_from || fieldVerificationData.field_verification.period_of_stay || '-'}</p>
                    </div>
                    <div>
                      <p className="text-blue-600 text-xs mb-1">Period of Stay To</p>
                      <p className="font-medium">{fieldVerificationData.field_verification.period_of_stay_to || 'Present'}</p>
                    </div>
                    <div>
                      <p className="text-blue-600 text-xs mb-1">Neighbour Confirmed</p>
                      <Badge className={fieldVerificationData.field_verification.neighbour_confirmed === 'yes' || fieldVerificationData.field_verification.neighbor_confirmed === 'yes' ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'}>
                        {fieldVerificationData.field_verification.neighbour_confirmed || fieldVerificationData.field_verification.neighbor_confirmed || '-'}
                      </Badge>
                    </div>
                    {(fieldVerificationData.field_verification.neighbour_confirmed === 'yes' || fieldVerificationData.field_verification.neighbor_confirmed === 'yes') && fieldVerificationData.field_verification.neighbor_name && (
                      <div>
                        <p className="text-blue-600 text-xs mb-1">Neighbour Name</p>
                        <p className="font-medium">{fieldVerificationData.field_verification.neighbor_name}</p>
                      </div>
                    )}
                  </div>
                  
                  {/* Person Met Details */}
                  {fieldVerificationData.field_verification.person_met_name && (
                    <div className="mt-4 pt-4 border-t border-blue-200">
                      <h5 className="font-medium text-xs mb-3 text-blue-700">Person Met at Location</h5>
                      <div className="grid grid-cols-3 gap-4 text-sm">
                        <div>
                          <p className="text-blue-600 text-xs mb-1">Name</p>
                          <p className="font-medium">{fieldVerificationData.field_verification.person_met_name}</p>
                        </div>
                        <div>
                          <p className="text-blue-600 text-xs mb-1">Relation</p>
                          <p className="font-medium capitalize">{fieldVerificationData.field_verification.person_met_relation || '-'}</p>
                        </div>
                        <div>
                          <p className="text-blue-600 text-xs mb-1">Contact</p>
                          <p className="font-medium">{fieldVerificationData.field_verification.person_met_contact || '-'}</p>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Remarks - Always show if present */}
              {(fieldVerificationData.field_verification?.overall_remarks || fieldVerificationData.field_verification?.remarks) && (
                <div className="bg-amber-50 p-4 rounded-lg border border-amber-200">
                  <h4 className="font-medium text-sm mb-2 text-amber-800 flex items-center gap-2">
                    <FileText className="w-4 h-4" />
                    Field Agent Remarks
                  </h4>
                  <p className="text-sm bg-white p-3 rounded border border-amber-200">{fieldVerificationData.field_verification.overall_remarks || fieldVerificationData.field_verification.remarks}</p>
                </div>
              )}

              {/* Ethics Declaration */}
              {fieldVerificationData.field_verification?.ethics_declaration && (
                <div className="bg-green-50 p-4 rounded-lg border border-green-200 flex items-center gap-3">
                  <CheckCircle className="w-6 h-6 text-green-600" />
                  <div>
                    <p className="font-medium text-green-800">Ethics Declaration Accepted</p>
                    <p className="text-sm text-green-600">Field agent has accepted the ethics and compliance declaration</p>
                  </div>
                </div>
              )}

              {/* Photos Section */}
              <div className="border rounded-lg p-4">
                <h4 className="font-medium text-sm mb-3 flex items-center gap-2">
                  <Camera className="w-4 h-4" />
                  Verification Photos (For Annexure)
                </h4>
                
                {/* Photos from field_verification.photos */}
                {fieldVerificationData.field_verification?.photos && fieldVerificationData.field_verification.photos.length > 0 && (
                  <div className="mb-4">
                    <p className="text-xs text-gray-500 mb-2">Field Verification Photos ({fieldVerificationData.field_verification.photos.length})</p>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                      {fieldVerificationData.field_verification.photos.map((photo, idx) => {
                        // Handle different photo formats - could be string URL, object with url/file_url, or reference {id, type}
                        let photoUrl = '';
                        let photoLabel = `Photo ${idx + 1}`;
                        let photoGps = null;
                        if (typeof photo === 'string') {
                          photoUrl = photo;
                        } else if (photo && typeof photo === 'object') {
                          photoUrl = photo.url || photo.file_url || '';
                          photoLabel = photo.label || photo.type?.replace(/_/g, ' ') || photoLabel;
                          photoGps = photo.gps_coordinates || (photo.gps_latitude && photo.gps_longitude ? `${photo.gps_latitude}, ${photo.gps_longitude}` : null);
                          // Resolve from annexures if only id/type reference
                          if (!photoUrl && photo.id && fieldVerificationPhotos.length > 0) {
                            const match = fieldVerificationPhotos.find(a => a.id === photo.id);
                            if (match) {
                              photoUrl = match.file_url || match.url || '';
                              if (!photoGps && (match.gps_latitude || match.gps_longitude)) {
                                photoGps = `${match.gps_latitude}, ${match.gps_longitude}`;
                              }
                              if (match.description) photoLabel = match.description;
                            }
                          }
                        }
                        const isValidUrl = photoUrl && typeof photoUrl === 'string' && (photoUrl.startsWith('http') || photoUrl.startsWith('data:'));
                        return (
                          <div 
                            key={idx} 
                            className="relative group cursor-pointer" 
                            onClick={() => {
                              if (isValidUrl) {
                                setPhotoPreview({
                                  url: photoUrl,
                                  label: photoLabel,
                                  gps: photoGps
                                });
                                setShowPhotoPreview(true);
                              } else {
                                toast.error('Photo URL is invalid or not available');
                              }
                            }}
                          >
                            {isValidUrl ? (
                              <img 
                                src={photoUrl} 
                                alt={photoLabel}
                                className="w-full h-28 object-cover rounded-lg border-2 border-gray-200 hover:border-indigo-400 transition-colors"
                                onError={(e) => {
                                  e.target.src = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="100" height="100"%3E%3Crect fill="%23ddd" width="100" height="100"/%3E%3Ctext fill="%23999" x="50%25" y="50%25" text-anchor="middle" dy=".3em"%3ENo Image%3C/text%3E%3C/svg%3E';
                                }}
                              />
                            ) : (
                              <div className="w-full h-28 bg-gray-200 rounded-lg border-2 border-gray-300 flex items-center justify-center">
                                <Camera className="w-8 h-8 text-gray-400" />
                              </div>
                            )}
                            <div className="absolute bottom-0 left-0 right-0 bg-black/70 text-white text-xs p-1.5 rounded-b-lg text-center">
                              {photoLabel}
                            </div>
                            {photoGps && (
                              <div className="absolute top-1 right-1 bg-green-500 text-white text-[9px] px-1.5 py-0.5 rounded font-medium">
                                GPS ✓
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
                
                {/* Photos from annexures API */}
                {fieldVerificationPhotos.length > 0 && (
                  <div>
                    <p className="text-xs text-gray-500 mb-2">Uploaded Documents ({fieldVerificationPhotos.length})</p>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                      {fieldVerificationPhotos.map((photo, idx) => {
                        const photoUrl = photo.file_url || photo.url;
                        const isValidUrl = photoUrl && (photoUrl.startsWith('http') || photoUrl.startsWith('data:'));
                        return (
                          <div 
                            key={photo.id || idx} 
                            className="relative group cursor-pointer" 
                            onClick={() => {
                              if (isValidUrl) {
                                setPhotoPreview({
                                  url: photoUrl,
                                  label: photo.annexure_type?.replace(/_/g, ' ') || photo.description || `Photo ${idx + 1}`,
                                  gps: photo.gps_latitude && photo.gps_longitude ? `${photo.gps_latitude}, ${photo.gps_longitude}` : null
                                });
                                setShowPhotoPreview(true);
                              } else {
                                toast.error('Photo URL is invalid or not available');
                              }
                            }}
                          >
                            {isValidUrl ? (
                              <img 
                                src={photoUrl} 
                                alt={photo.annexure_type || `Photo ${idx + 1}`}
                                className="w-full h-28 object-cover rounded-lg border-2 border-gray-200 hover:border-indigo-400 transition-colors"
                                onError={(e) => {
                                  e.target.src = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="100" height="100"%3E%3Crect fill="%23ddd" width="100" height="100"/%3E%3Ctext fill="%23999" x="50%25" y="50%25" text-anchor="middle" dy=".3em"%3ENo Image%3C/text%3E%3C/svg%3E';
                                }}
                              />
                            ) : (
                              <div className="w-full h-28 bg-gray-200 rounded-lg border-2 border-gray-300 flex items-center justify-center">
                                <Camera className="w-8 h-8 text-gray-400" />
                              </div>
                            )}
                            <div className="absolute bottom-0 left-0 right-0 bg-black/70 text-white text-xs p-1.5 rounded-b-lg text-center">
                              {photo.annexure_type?.replace(/_/g, ' ') || `Photo ${idx + 1}`}
                            </div>
                            {photo.gps_latitude && photo.gps_longitude && (
                              <div className="absolute top-1 right-1 bg-green-500 text-white text-[9px] px-1.5 py-0.5 rounded font-medium">
                                GPS ✓
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
                
                {/* No Photos */}
                {(!fieldVerificationData.field_verification?.photos || fieldVerificationData.field_verification.photos.length === 0) && fieldVerificationPhotos.length === 0 && (
                  <div className="text-center py-6 text-gray-400">
                    <Camera className="w-10 h-10 mx-auto mb-2 text-gray-300" />
                    <p className="text-sm">No photos uploaded yet</p>
                  </div>
                )}
                
                {/* Note about Annexure */}
                <div className="mt-4 p-3 bg-blue-50 rounded-lg border border-blue-200">
                  <p className="text-xs text-blue-700 flex items-center gap-2">
                    <FileText className="w-4 h-4" />
                    These photos will be available in the Document Upload section and can be used as Annexures in Final/Interim Reports
                  </p>
                </div>
              </div>

              {/* No Data Message */}
              {!fieldVerificationData.field_verification?.submitted && fieldVerificationPhotos.length === 0 && (
                <div className="text-center py-8 bg-gray-50 rounded-lg border-2 border-dashed border-gray-200">
                  <FileText className="w-12 h-12 mx-auto mb-2 text-gray-300" />
                  <p className="font-medium text-gray-600">Field verification not yet submitted</p>
                  <p className="text-sm text-gray-400 mt-1">The field agent has not completed the verification form</p>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Photo Preview Modal */}
      {showPhotoPreview && photoPreview && (
        <div 
          className="fixed inset-0 bg-black/90 z-[100] flex items-center justify-center p-4"
          onClick={() => setShowPhotoPreview(false)}
        >
          <div 
            className="bg-white rounded-lg max-w-3xl w-full max-h-[90vh] overflow-hidden shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="p-4 border-b flex items-center justify-between bg-gray-50">
              <div>
                <h3 className="font-semibold text-gray-800">{photoPreview.label}</h3>
                {photoPreview.gps && (
                  <p className="text-xs text-green-600 flex items-center gap-1 mt-1">
                    <MapPin className="w-3 h-3" />
                    GPS: {photoPreview.gps}
                  </p>
                )}
              </div>
              <Button 
                size="sm" 
                variant="ghost" 
                onClick={() => setShowPhotoPreview(false)}
                className="text-gray-500 hover:text-gray-700"
              >
                <X className="w-5 h-5" />
              </Button>
            </div>
            <div className="p-4 bg-gray-100 flex items-center justify-center" style={{ maxHeight: 'calc(90vh - 140px)' }}>
              <img 
                src={photoPreview.url} 
                alt={photoPreview.label}
                className="max-w-full max-h-[65vh] object-contain rounded shadow-lg"
                onError={(e) => {
                  e.target.src = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="400" height="300"%3E%3Crect fill="%23f0f0f0" width="400" height="300"/%3E%3Ctext fill="%23999" x="50%25" y="50%25" text-anchor="middle" dy=".3em" font-size="18"%3EImage failed to load%3C/text%3E%3C/svg%3E';
                }}
              />
            </div>
            <div className="p-4 border-t flex justify-between items-center bg-gray-50">
              <Button 
                variant="outline" 
                onClick={() => setShowPhotoPreview(false)}
              >
                Close
              </Button>
              <Button 
                variant="default"
                onClick={() => window.open(photoPreview.url, '_blank')}
              >
                <ExternalLink className="w-4 h-4 mr-2" />
                Open in New Tab
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
